import { z } from 'zod/v4';
import {
  EEntityType,
  ERelationType,
  NumberString,
} from '../text-relation-type.enums';
import { ETextType } from './text.enums';
import { Brand } from '../../branded-types';
import { LanguagePrefix, LANGUAGE_PREFIX_LENGTH } from '../language.interfaces';

export type TextId = Brand<string, 'textId'>;
export type TextLanguageValueId = Brand<string, 'textLanguageValueId'>;
export type NormalTextId = Brand<string, 'normalTextId'>;

export const TEXT_LANGUAGE_VALUE_SCHEMA: z.ZodType<ITextLanguageValue> =
  z.object({
    id: z
      .uuid()
      .transform((s: string) => s as TextLanguageValueId)
      .optional(),
    languagePrefix: z
      .string()
      .min(LANGUAGE_PREFIX_LENGTH)
      .max(LANGUAGE_PREFIX_LENGTH)
      .transform((s: string) => s as LanguagePrefix)
      .default('DE' as LanguagePrefix),
    value: z.string(),
  });
export interface ITextLanguageValue {
  id?: TextLanguageValueId;
  languagePrefix: LanguagePrefix;
  value: string;
}

export const TEXT_SCHEMA: z.ZodType<IText> = z.object({
  id: z
    .uuid()
    .transform((s: string) => s as TextId)
    .optional(),
  values: z.array(TEXT_LANGUAGE_VALUE_SCHEMA),
  lastEditedAt: z.iso.datetime().optional(),
});

export interface IText {
  id?: TextId;
  values: (ITextLanguageValue | ITextLanguageChangedValue)[];
  lastEditedAt?: string;
}

export const NORMAL_TEXT_SCHEMA: z.ZodType<INormalText> = z.object({
  id: z
    .uuid()
    .transform((s: string) => s as NormalTextId)
    .optional(),
  textType: z.enum(ETextType),
  entityType: z.string(),
  relationType: z.string(),
  text: TEXT_SCHEMA,
});
export const NORMAL_TEXTS_SCHEMA: z.ZodType<INormalText[]> =
  z.array(NORMAL_TEXT_SCHEMA);
export interface INormalText {
  id?: NormalTextId;
  textType: ETextType;
  entityType: EEntityType | string; // e.g., 'Footer', 'Header', ...
  relationType: ERelationType | NumberString | string; // e.g., 'title', 'accountName',...
  text: IText;
}

export const TEMPLATE_ENTITY_RELATIONS: Partial<
  Record<EEntityType, Partial<Record<ERelationType, string[]>>>
> = {
  [EEntityType.subscription]: {
    [ERelationType.sepaAgreement]: ['shopOwner'],
  },
  [EEntityType.proofOfPurchaseDate]: {
    [ERelationType.proofOfPurchaseDate]: ['ProofOfPurchaseDate'],
  },
  [EEntityType.proofOfPurchaseNumber]: {
    [ERelationType.proofOfPurchaseNumber]: ['ProofOfPurchaseNumber'],
  },
  [EEntityType.shoppingCart]: {
    [ERelationType.maxAmountExceeded]: ['maxAmount'],
  },
};

export const isTemplateText: (
  entity: EEntityType,
  relation: ERelationType
) => boolean = (entity: EEntityType, relation: ERelationType): boolean => {
  return !!TEMPLATE_ENTITY_RELATIONS[entity]?.[relation];
};

export const getTemplateText: (
  entity: EEntityType,
  relation: ERelationType
) => string[] = (entity: EEntityType, relation: ERelationType): string[] => {
  return TEMPLATE_ENTITY_RELATIONS[entity]?.[relation]
    ? TEMPLATE_ENTITY_RELATIONS[entity][relation]
    : [];
};

export interface ITextLanguageChangedValue extends ITextLanguageValue {
  isChanged?: boolean;
}

const DEFAULT_WEB_HEADER_TEXTS: INormalText[] = [
  {
    id: 'f65412fe-ae05-42b5-86b2-5ddeb5352709' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.header,
    relationType: ERelationType.loading,
    text: {
      id: 'a5384d7f-dbee-48f7-87ac-b75a7b4c58ac' as TextId,
      values: [
        {
          id: '7e45ccb3-ca47-4540-bbc7-134d7acc6718' as TextLanguageValueId,
          value: 'Header laden',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
];

const DEFAULT_WEB_BANNER_TEXTS: INormalText[] = [
  {
    id: 'fb4c6537-941f-42af-9017-13923cb7fc3c' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.banner,
    relationType: ERelationType.loading,
    text: {
      id: 'd8f9c2d9-0ae4-4bc1-907a-c9f148b1f0ab' as TextId,
      values: [
        {
          id: '35056164-345f-4ae5-9737-498132dfc5e5' as TextLanguageValueId,
          value: 'Banner laden',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
];

const DEFAULT_WEB_FOOTER_TEXTS: INormalText[] = [
  {
    id: 'ee19418e-9382-49cf-983b-a9de263b9d3a' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.footer,
    relationType: ERelationType.loading,
    text: {
      id: '94346ec0-6e4d-4833-98ff-3b823179bf4b' as TextId,
      values: [
        {
          id: '2eaab745-dc96-49b7-a636-26e7c325d2c6' as TextLanguageValueId,
          value: 'Footer laden',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
];

const DEFAULT_WEB_TICKET_TEXTS: INormalText[] = [
  {
    id: '8e894449-feeb-4ede-b9c5-9eca8e4b8bd5' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.ticket,
    relationType: ERelationType.loading,
    text: {
      id: 'a792ebf0-4a55-413e-bf18-108eee55ab6c' as TextId,
      values: [
        {
          id: '06f22231-dce0-49ac-ac57-f04aefcd376d' as TextLanguageValueId,
          value: 'Tickets laden',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
];

const DEFAULT_WEB_TICKET_CONFIGURATION_TEXTS: INormalText[] = [
  {
    id: 'f2e53d47-1fdc-448b-88e5-dda8b1112521' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.ticketConfiguration,
    relationType: ERelationType.travelData,
    text: {
      id: 'b39a1a58-7c63-418a-aeb2-e68643f36e2c' as TextId,
      values: [
        {
          id: '474f7f7f-0b7f-40fb-a071-0528fb234f25' as TextLanguageValueId,
          value: 'Reisedatum',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '037f9293-416d-40f1-a032-9cf6343e9984' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.ticketConfiguration,
    relationType: ERelationType.loadingDirectTickets,
    text: {
      id: '168ac961-d71f-418d-af59-292979b93014' as TextId,
      values: [
        {
          id: '1b36d8e7-c9e3-4c27-b723-da0667835f66' as TextLanguageValueId,
          value: 'Direkttickets laden',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '91f5e1b3-24f8-4954-a45b-bd2fed37bd43' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.ticketConfiguration,
    relationType: ERelationType.loadingDirectTicketOptions,
    text: {
      id: 'ac4a8bdc-d104-4b33-b200-f2b6d8aaf210' as TextId,
      values: [
        {
          id: '118dddc8-1b95-4cf0-b028-69037adbaea7' as TextLanguageValueId,
          value: 'Direktticketoptionen laden',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd3106b13-7275-4979-bde2-491b78ab2e2a' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.ticketConfiguration,
    relationType: ERelationType.loadingDirectTicketPersonalData,
    text: {
      id: 'c8a2c94b-0f52-447d-a0da-6ee58d7d2f4d' as TextId,
      values: [
        {
          id: 'da65d544-26e4-4fe5-b3cc-a6f443648971' as TextLanguageValueId,
          value: 'Persönliche Daten laden',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'ed55d3dd-f739-4822-b714-012753aca366' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.ticketConfiguration,
    relationType: ERelationType.loadingDirectTicketOwnerData,
    text: {
      id: 'c46177af-9093-48b2-9037-b11873a5088e' as TextId,
      values: [
        {
          id: 'beeaec03-fe25-4040-b440-142ca2303151' as TextLanguageValueId,
          value: 'Fahrgastdaten laden',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '7be8608f-2e03-4820-9ac9-f6dc6a255676' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.ticketConfiguration,
    relationType: ERelationType.loadingDirectTicketCheckout,
    text: {
      id: '5303383e-0eb8-4b6f-8479-2f80032e198c' as TextId,
      values: [
        {
          id: '8677c674-357e-4ec9-b7de-45afe6e8fe67' as TextLanguageValueId,
          value: 'Kassendaten laden',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd0224083-837a-4f4f-923f-8f02ced11a2f' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.ticketConfiguration,
    relationType: ERelationType.loadingDirectTicketValidation,
    text: {
      id: '051aab55-a07c-41ba-86f7-23d28e1bc9ac' as TextId,
      values: [
        {
          id: '8ac77dbd-5ca4-47fb-96ca-5ad3ca59d16a' as TextLanguageValueId,
          value: 'Warte auf Ticketvalidierung',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '0001651c-5fc1-4cc1-9db7-f81e888f66f5' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.ticketConfiguration,
    relationType: ERelationType.defaultPaymentProvider,
    text: {
      id: 'c16faf62-979b-49e3-924e-7d355faba2ed' as TextId,
      values: [
        {
          id: '7ca19b14-e8e8-4ee6-b97b-f8e0fd97ee90' as TextLanguageValueId,
          value: 'LOGPAY',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'eaed5acf-e7d0-41db-bd5a-de56a08f920d' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.ticketConfiguration,
    relationType: ERelationType.ticketIsForMe,
    text: {
      id: 'd78dafdf-fc65-4a6e-9895-9539ce4ada01' as TextId,
      values: [
        {
          id: '91aa0312-bac0-43f6-8287-29fde814fcb1' as TextLanguageValueId,
          value: 'Das Ticket ist für mich',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '27027a15-689e-4069-993d-0341d102bd39' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.ticketConfiguration,
    relationType: ERelationType.saveFellowTravelersLabel,
    text: {
      id: '4f46b14d-3e9b-44c8-9ad6-104fe7816937' as TextId,
      values: [
        {
          id: 'b29b1b62-56f9-441f-9bc4-8fc40ed091ed' as TextLanguageValueId,
          value: 'Mitreisende und alternative Ticketbesitzer speichern',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
];

const DEFAULT_WEB_DIRECT_TICKET_TEXTS: INormalText[] = [
  {
    id: 'd4224083-837a-4f4f-923f-8f02ced11a2f' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.directTicketsPageHeading,
    text: {
      id: '351aab55-a07c-41ba-86f7-23d28e1bc9ac' as TextId,
      values: [
        {
          id: '8ac27dbd-5ca4-47fb-96ca-5ad3ca59d16a' as TextLanguageValueId,
          value: 'Festpreis',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '176900bd-bae5-44d9-8c37-7e7aee53e210' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.fixedPriceHeading,
    text: {
      id: 'b6d8a03c-b6bb-4f6f-a4bc-8340da925951' as TextId,
      values: [
        {
          id: '40bb1745-0ec6-485a-8b15-1ded8759cada' as TextLanguageValueId,
          value: 'Festpreis',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'ee249b0d-f401-4539-8400-7129b4fb4990' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.relationHeading,
    text: {
      id: 'd6f15df2-57af-4e29-b21d-412e60f7d26d' as TextId,
      values: [
        {
          id: '1af7a9c2-c5ee-4600-91b9-c09efe899a8e' as TextLanguageValueId,
          value: 'Relationspreis',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '50951ce9-5f22-4020-815a-5f2626156bbd' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.ticketOptions,
    text: {
      id: '78405806-5ea6-49ef-999b-bfd6752dd8e6' as TextId,
      values: [
        {
          id: 'a3867751-ada4-445d-a742-fa5bb4959351' as TextLanguageValueId,
          value: 'Ticketoptionen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '18aa442f-daf1-4213-a2bc-f400ce0b598e' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.payment,
    text: {
      id: 'd107745f-c014-4f8a-9ad6-ec156defd355' as TextId,
      values: [
        {
          id: '3c11d6a1-52be-471b-b559-0f3cc2c52283' as TextLanguageValueId,
          value: 'Kasse',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '9d8365ed-ee19-4e58-9b3a-8b9d49ecb099' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.subscription,
    relationType: ERelationType.payment,
    text: {
      id: '0438e0ea-f8a3-4ce0-a3a2-02169070e8d4' as TextId,
      values: [
        {
          id: '0a5cc41d-eda7-4b5d-8a99-49fdab9cb5a3' as TextLanguageValueId,
          value: 'Kasse',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd5696ec4-e971-4b6e-9bde-34f889a7806a' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.checkout,
    text: {
      id: '89ee9b67-d80d-441e-bd13-f1f9d06a4530' as TextId,
      values: [
        {
          id: '4f45817d-c7fa-4d56-974c-d836626dbd09' as TextLanguageValueId,
          value: 'Kasse',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '5691e42e-42a6-4682-80fd-ac68bfb24b7f' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.passengerData,
    text: {
      id: 'c014be1f-8592-4171-be7e-ce2432536fc7' as TextId,
      values: [
        {
          id: '029c94dc-eefd-43ab-bb1b-724dffb81c8d' as TextLanguageValueId,
          value: 'Fahrgastdaten',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'db51f8f0-6feb-4eb0-833c-b4b04e9c0e28' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.finePrint,
    text: {
      id: '28256829-2340-4265-bf5f-d6d8e138d503' as TextId,
      values: [
        {
          id: '34692598-8772-400f-a0c4-72d0d65813f3' as TextLanguageValueId,
          value:
            'Hier könnten noch detaillierte Informationen stehen, die sich eh keiner durchliest. Das kleingedruckte quasi. Es ist nicht erlaubt in den Zügen von AMCON zu Darten. Stattdessen wird empfohlen ein Ballwurf-Turnier durch die Abteile zu starten. Wer gewinnt, darf ein EBE verteilen.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'c996efcb-2fa5-4b3c-b136-16998b5fe174' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.receiveTicketInfo,
    text: {
      id: '07dcfd13-63a1-4422-8298-a2cbaef5ab42' as TextId,
      values: [
        {
          id: '2baedb85-b264-4d12-ad1d-1fb85014dc92' as TextLanguageValueId,
          value:
            'Digitales Ticket per E-Mail <br/> Das digitale Ticket wird Ihnen als E-Mail zugesendet. Das Ticket befindet sich als PDF im Anhang.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '7fc835d6-235e-448e-b41b-64006790d66e' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.paymentProviderInfo,
    text: {
      id: '2361ccf8-b711-4a54-b6ff-a7a61b6b7955' as TextId,
      values: [
        {
          id: '2513a165-c6c2-4c45-87ef-52d422ebf6bd' as TextLanguageValueId,
          value:
            'Mit Bestätigung Ihrer Bestellung werden Sie ggf. auf den Zahlungsdienstleister weitergeleitet, um dort Ihre Zahlung und Zahlungsart zu bestätigen.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '28b9faf0-921d-4f90-91cf-2e3a2440bfe4' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.noPaymentType,
    text: {
      id: '89c343e4-cc9a-4b8d-a9b5-1a4a85c9332c' as TextId,
      values: [
        {
          id: '4a6aee4d-784d-4b5d-a77d-58ed5dc1a6ff' as TextLanguageValueId,
          value:
            'Sonstiges <br/> Zahlungsart wird vor Kaufabschluss ausgewählt/angegeben',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'ae35f5d8-f617-4739-92dd-c999e0f05e2e' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.receiveTicket,
    text: {
      id: 'b2c0058e-171f-45f1-a401-b217ea46d9a1' as TextId,
      values: [
        {
          id: '4b8e9830-f13e-4088-852e-f11f39efa81a' as TextLanguageValueId,
          value: 'Erhalt des Tickets',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'a0181a1a-1fa5-499f-ac9a-feab028df5bc' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.paymentType,
    text: {
      id: 'f1c22659-4cd7-4933-a49f-9bd3db0464d4' as TextId,
      values: [
        {
          id: '62f9b7da-b0f9-4041-8da8-5bdb7231f034' as TextLanguageValueId,
          value: 'Zahlungsart',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '1bdf518a-1ce0-4c4c-899c-45665de52857' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.paymentProvider,
    text: {
      id: '90476dad-07f4-4c13-91f7-867604a67855' as TextId,
      values: [
        {
          id: 'adb8d01b-f597-4933-bef4-38b6cdce6f14' as TextLanguageValueId,
          value: 'Zahlungsdienst',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '68bdb485-6890-4c8d-99bf-5d88fa64ab28' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.privateDataHint,
    text: {
      id: '25dd2d7d-a4f8-43a6-819c-c3e1294fe249' as TextId,
      values: [
        {
          id: '7a8b0eb7-b449-43c6-960b-b6cca02aac23' as TextLanguageValueId,
          value:
            'Änderungen an den Personendaten können Sie über die Konto-Einstellungen vornehmen.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'f39f598e-525a-4cdb-b9f5-1ab24913216a' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.paymentAddress,
    text: {
      id: 'e186f0e3-a4d5-41c2-9c82-50f509f08c1e' as TextId,
      values: [
        {
          id: '6804fd54-d322-44b5-b3d3-58ba56b487cf' as TextLanguageValueId,
          value: 'Rechnungsadresse',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '60fbaa25-1ccb-40d6-9a2a-e3fc257882b3' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.ticketAmount,
    text: {
      id: 'e55953bd-a9eb-43a2-be9a-1105348a5c26' as TextId,
      values: [
        {
          id: '0745672b-7d43-4c99-b68a-9756d1938015' as TextLanguageValueId,
          value: 'Menge Tickets',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '1973bef7-b6dc-45f0-b177-aded3e93e502' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.numberOfPeople,
    text: {
      id: '9f40ae48-6dbc-41e0-8fff-ef242253b7c1' as TextId,
      values: [
        {
          id: '171db0fa-1292-4c12-80ee-c104b259cb4b' as TextLanguageValueId,
          value: 'Personen Anzahl pro Ticket',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'b64d91de-8028-4a4b-8703-6a4ed30b4096' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.persons,
    text: {
      id: 'a88707e4-83ec-4cb7-aa20-9990acc19109' as TextId,
      values: [
        {
          id: '641aade4-4834-4747-88b1-18d24a746492' as TextLanguageValueId,
          value: 'Personen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'e015b8a5-e98e-49a2-9b44-d2e354b03abc' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.continueAsGuest,
    text: {
      id: 'd636f4ab-c04c-453d-b7ce-ca48c829cc13' as TextId,
      values: [
        {
          id: '4bc77a7c-f92d-48a7-90e6-efae495e9e2a' as TextLanguageValueId,
          value: 'Weiter als Gast',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '2ba19fe5-829a-4e9d-9c08-45233066ee3c' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.passwordError,
    text: {
      id: 'bbb6f81e-156b-4be5-89fd-a3f9b433264c' as TextId,
      values: [
        {
          id: '1b5f7ec8-35b3-4c42-b74a-455df8192675' as TextLanguageValueId,
          value: 'Passwort Fehler',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '7d0f5c63-ec81-45d2-9e5d-a043894ae771' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.headline,
    text: {
      id: '8f2d1523-32a5-49f1-bd94-b5232e0ae072' as TextId,
      values: [
        {
          id: 'c84660dc-8a79-415b-b21d-0a03fc876fac' as TextLanguageValueId,
          value: 'Vergangene Bestellungen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'c08c1718-70e0-406f-9077-6b3ad6b2e8b7' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.thirtyDays,
    text: {
      id: '57643fde-1b41-477a-98d8-2fa89bcfa053' as TextId,
      values: [
        {
          id: '53be247f-3a89-4bf7-9fc9-d16d264238d7' as TextLanguageValueId,
          value: 'Letzte 30 Tage',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '437385e9-79ee-48cf-b972-ab1f191cb9da' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.sixMonths,
    text: {
      id: '6faf7169-21f0-4806-a1c1-580602b4ae47' as TextId,
      values: [
        {
          id: '3d246e35-eaa3-4b34-829e-10e8ecc03a54' as TextLanguageValueId,
          value: 'Letzte 6 Monate',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd016086a-8af1-4eed-81b7-41f4db938b94' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.allTime,
    text: {
      id: '835637a6-5f1d-403b-9230-6a189ad5bb45' as TextId,
      values: [
        {
          id: '1647ae09-2555-44ce-a029-1f9a9f279c05' as TextLanguageValueId,
          value: 'Gesamter Zeitraum',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '7579435b-2848-4c59-a2f5-39b68e0a0170' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.travelData,
    text: {
      id: '4b576e27-673f-4c91-99a5-60588c9d434e' as TextId,
      values: [
        {
          id: '776efe2b-5349-42ba-a8e0-9a0df05a6f48' as TextLanguageValueId,
          value: 'Reisedatum',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'b0b8ba39-4632-4917-ae27-9f5c2ff36ad6' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.status,
    text: {
      id: 'b7506d40-2944-46d0-a008-e74ab6c08965' as TextId,
      values: [
        {
          id: '4a519f61-9528-4eab-b6af-df16a12ad7e7' as TextLanguageValueId,
          value: 'Ticketstatus',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd935eda4-58d6-4e14-91b6-22bbc81cdcab' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.info,
    text: {
      id: 'ccae0da9-2d3f-49ec-8d33-54670f1d7405' as TextId,
      values: [
        {
          id: '2a69b5d8-cdc5-46f3-8238-8142fa9347d1' as TextLanguageValueId,
          value: 'Ticketinformationen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '9f27dd71-ae30-4218-87ea-6bd9eb5bd426' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.price,
    text: {
      id: '34a929aa-41e5-4d72-82d6-9d5f48c22110' as TextId,
      values: [
        {
          id: 'd51835e5-50ad-4ceb-9561-547347fd6bf5' as TextLanguageValueId,
          value: 'Preis',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd4ae1778-09fd-4624-ab5a-5742c91d8714' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.orderDate,
    text: {
      id: '5a24af0b-19ba-49f2-84fe-f22540a50fca' as TextId,
      values: [
        {
          id: '60622b0d-7ea9-4d2c-ba90-bde3a78cb48a' as TextLanguageValueId,
          value: 'Bestelldatum',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'c53cb6b1-76a6-448b-a416-60ac9e667f0b' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.orderStatus,
    text: {
      id: 'c566e69f-2a28-433a-ba21-ec39eeb0b4b1' as TextId,
      values: [
        {
          id: 'ce11312e-066a-4b9c-a9de-8a71d3a79909' as TextLanguageValueId,
          value: 'Bestellstatus',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '90fe2c47-c3ed-420f-855f-c08bc21e6e9d' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.orderNumber,
    text: {
      id: '809695a2-864a-45f5-b929-816073a30cd2' as TextId,
      values: [
        {
          id: '2c322a1d-8b07-4441-a8b7-9f6e1b303f7e' as TextLanguageValueId,
          value: 'Bestellnummer',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '010d61cb-896f-474e-a3b5-ee4f3fbad8a4' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.paymentType,
    text: {
      id: '0e80939b-67b5-4865-bf45-ce7e63220445' as TextId,
      values: [
        {
          id: '8d39dd5c-fc67-4abc-b75b-702599ce47e3' as TextLanguageValueId,
          value: 'Zahlungsart',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '9f04fbc4-6879-49d0-a1c7-839991fa8e9f' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.amazonPay,
    text: {
      id: '9f04fbc4-6879-49d0-a1c7-839991fa8e9f' as TextId,
      values: [
        {
          id: '9f04fbc4-6879-49d0-a1c7-839991fa8e9f' as TextLanguageValueId,
          value: 'Amazon Pay',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'a7f287d6-2a3b-472c-945e-31e5313a905e' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.sepaDebit,
    text: {
      id: 'a7f287d6-2a3b-472c-945e-31e5313a905e' as TextId,
      values: [
        {
          id: 'a7f287d6-2a3b-472c-945e-31e5313a905e' as TextLanguageValueId,
          value: 'Lastschrift',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'f7dcdbb1-172b-4f82-a17a-ab80b84573df' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.creditCard,
    text: {
      id: 'f7dcdbb1-172b-4f82-a17a-ab80b84573df' as TextId,
      values: [
        {
          id: 'f7dcdbb1-172b-4f82-a17a-ab80b84573df' as TextLanguageValueId,
          value: 'Kreditkarte',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '3f4071fe-fc69-4de1-99b1-fc3c82db3253' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.payDirect,
    text: {
      id: '3f4071fe-fc69-4de1-99b1-fc3c82db3253' as TextId,
      values: [
        {
          id: '3f4071fe-fc69-4de1-99b1-fc3c82db3253' as TextLanguageValueId,
          value: 'Paydirekt',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '9c61988d-e894-4109-b85d-36c72f618a00' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.googlePay,
    text: {
      id: '9c61988d-e894-4109-b85d-36c72f618a00' as TextId,
      values: [
        {
          id: '9c61988d-e894-4109-b85d-36c72f618a00' as TextLanguageValueId,
          value: 'Google Pay',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '136fa289-26d6-4f66-997c-dc9747a4a93b' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.paypal,
    text: {
      id: '136fa289-26d6-4f66-997c-dc9747a4a93b' as TextId,
      values: [
        {
          id: '136fa289-26d6-4f66-997c-dc9747a4a93b' as TextLanguageValueId,
          value: 'PayPal',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '2c998d01-b753-44a4-b17b-badfea372ba1' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.applePay,
    text: {
      id: '2c998d01-b753-44a4-b17b-badfea372ba1' as TextId,
      values: [
        {
          id: '2c998d01-b753-44a4-b17b-badfea372ba1' as TextLanguageValueId,
          value: 'Apple Pay',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'e3bcf251-e737-4d91-8aba-2a3a98fc70c4' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.prePay,
    text: {
      id: 'e3bcf251-e737-4d91-8aba-2a3a98fc70c4' as TextId,
      values: [
        {
          id: 'e3bcf251-e737-4d91-8aba-2a3a98fc70c4' as TextLanguageValueId,
          value: 'Prepay',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '7600bd2e-b59a-4058-9b0c-8dc40582710c' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.bonVoyo,
    text: {
      id: '7600bd2e-b59a-4058-9b0c-8dc40582710c' as TextId,
      values: [
        {
          id: '7600bd2e-b59a-4058-9b0c-8dc40582710c' as TextLanguageValueId,
          value: 'Bonvoyo',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'a38145c8-ab79-4727-a080-0fbdc89de8cb' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.complete,
    text: {
      id: 'a38145c8-ab79-4727-a080-0fbdc89de8cb' as TextId,
      values: [
        {
          id: 'a38145c8-ab79-4727-a080-0fbdc89de8cb' as TextLanguageValueId,
          value: 'Abgeschlossen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '6203fc95-c4ee-4680-8d4a-87db9eb2394d' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.inProgress,
    text: {
      id: '6203fc95-c4ee-4680-8d4a-87db9eb2394d' as TextId,
      values: [
        {
          id: '6203fc95-c4ee-4680-8d4a-87db9eb2394d' as TextLanguageValueId,
          value: 'In Bearbeitung',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '479980d6-08a0-4ea5-a65f-1e1452c11b81' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.pendingPaymentVerification,
    text: {
      id: '479980d6-08a0-4ea5-a65f-1e1452c11b81' as TextId,
      values: [
        {
          id: '479980d6-08a0-4ea5-a65f-1e1452c11b81' as TextLanguageValueId,
          value: 'Benötigt Verifizierung',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'f0aca4fb-c210-4dcb-8f43-6f5e002e4ce3' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.cancelled,
    text: {
      id: 'f0aca4fb-c210-4dcb-8f43-6f5e002e4ce3' as TextId,
      values: [
        {
          id: 'f0aca4fb-c210-4dcb-8f43-6f5e002e4ce3' as TextLanguageValueId,
          value: 'Abgebrochen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'fa57a7c0-4bd1-44e8-9201-d930d6846d6c' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.rejected,
    text: {
      id: 'fa57a7c0-4bd1-44e8-9201-d930d6846d6c' as TextId,
      values: [
        {
          id: 'fa57a7c0-4bd1-44e8-9201-d930d6846d6c' as TextLanguageValueId,
          value: 'Storniert',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '8dc39c46-8b38-4e13-916b-2b68a5c3a6e1' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.unknown,
    text: {
      id: '8dc39c46-8b38-4e13-916b-2b68a5c3a6e1' as TextId,
      values: [
        {
          id: '8dc39c46-8b38-4e13-916b-2b68a5c3a6e1' as TextLanguageValueId,
          value: 'Unbekannt',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'c6210be4-a08f-489d-bbc5-5c11ecd6cc54' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.ticketAmountDecreaseDisabled,
    text: {
      id: '3ad03352-d722-4258-bd6d-4ff066476f0b' as TextId,
      values: [
        {
          id: '6cc8a121-3005-4dc0-996b-a47cdb2b4e28' as TextLanguageValueId,
          value: 'Menge kann nicht weiter verringert werden.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '27d39b3a-c2a5-4b07-ba30-4ab18d17e434' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.directTicket,
    relationType: ERelationType.ticketAmountIncreaseDisabled,
    text: {
      id: '10e81952-a1b2-48e7-b55e-f805b30e2363' as TextId,
      values: [
        {
          id: 'c5bf85f7-dc59-45f4-82ce-6ac6b9270a92' as TextLanguageValueId,
          value: 'Menge kann nicht weiter erhöht werden.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '5651ae3d-1a30-44fa-8dc5-c9dad894a8c8' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.relationSearch,
    relationType: ERelationType.switchDisabled,
    text: {
      id: '041a317c-4ca6-4f63-a0dd-825ce13ff8f9' as TextId,
      values: [
        {
          id: '42f5ab23-c355-4735-8ac0-25940600fa94' as TextLanguageValueId,
          value: 'Start/Ziel-Wechsel nicht möglich.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '09f9bdb9-ba79-4bd3-a990-ffb387621a91' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.searchDisabled,
    text: {
      id: '350f68d2-e40a-4ed4-97ab-4d724cc39a16' as TextId,
      values: [
        {
          id: 'ee3c9291-a67f-45ab-8fb9-d25686596743' as TextLanguageValueId,
          value: 'Suche nicht möglich.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
];

const DEFAULT_WEB_SHOPPING_CART_TEXTS: INormalText[] = [
  {
    id: 'd4f0cf76-0a73-4e7f-8e94-23949c621bbf' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.shoppingCartLabel,
    text: {
      id: '5b832447-17b0-4ad3-807b-946e348d4380' as TextId,
      values: [
        {
          id: '934dc8c0-6099-44f1-b162-09d8306ac808' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Warenkorb',
        },
      ],
    },
  },
  {
    id: 'a5f6f9c2-7d4f-45aa-b4d5-04f1d38af61d' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.shoppingCartEmptyText,
    text: {
      id: 'f5f27b1f-8f5a-4c9f-96b1-47aa1990ecf5' as TextId,
      values: [
        {
          id: '2f22a6e0-13c7-44b3-b2d1-8a67f1b31fa4' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Der Warenkorb ist zurzeit leer.',
        },
      ],
    },
  },
  {
    id: '9bfc0c83-637c-4d4c-89cf-15825b218731' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.shoppingCartTicketEditLabel,
    text: {
      id: '13a4c472-d830-4935-b3ea-091fc541c24a' as TextId,
      values: [
        {
          id: '0f7d5685-ec26-4b74-9b26-982ba963237a' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Fahrschein bearbeiten',
        },
      ],
    },
  },
  {
    id: 'ef0aa7dc-54fb-4c4f-89fb-3af0c1ab6990' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.shoppingCartPassengerDataLabel,
    text: {
      id: '59381534-89c0-4c2b-9c54-882f5c6c727f' as TextId,
      values: [
        {
          id: '6e329da9-3c82-45db-b10f-932f0b2c826d' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Fahrgastdaten bearbeiten',
        },
      ],
    },
  },
  {
    id: 'c7d1c1c3-8b3e-42ef-ae7e-4d9d55c823b0' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.shoppingCartTicketInfoLabel,
    text: {
      id: '93f76d74-94d4-4b2a-9670-3d41d1869c6f' as TextId,
      values: [
        {
          id: '31ec4075-8377-4f92-9f3b-72eb285f4517' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Ticketinformationen ansehen',
        },
      ],
    },
  },
  {
    id: '44e658c0-f72a-4d71-8c09-8280e0b6b0c0' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.shoppingCartTicketDeleteLabel,
    text: {
      id: '82af5a6a-b33f-4c52-a871-90f2f0a84a9d' as TextId,
      values: [
        {
          id: '7089d2c2-8b0f-493f-b4e0-ff6e8d76a89b' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Ticket entfernen',
        },
      ],
    },
  },
  {
    id: 'ddf6c709-4561-4f44-a820-2f03caa8f6a2' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.shoppingCartCountLabel,
    text: {
      id: 'dff94312-dc8c-4f2b-b71f-6d58c23d09fb' as TextId,
      values: [
        {
          id: '23e85a9c-ef93-4495-b7a4-ec7b7c024d64' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Anzahl',
        },
      ],
    },
  },
  {
    id: '468f62f7-8c3c-4bc3-9a58-90a2141d9f26' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.shoppingCartPriceNetLabel,
    text: {
      id: '4e8ebfa3-b8a7-48c6-828b-0a04a13ad62d' as TextId,
      values: [
        {
          id: 'dd8aab62-bf24-43e8-8c4b-09f6d7a6f0c4' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Nettopreis',
        },
      ],
    },
  },
  {
    id: 'a62eb3cd-26a2-47e2-b83f-df84e55b2699' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.shoppingCartVatLabel,
    text: {
      id: '11f3b7df-b5fb-4f49-962a-d0d2b7f62c2c' as TextId,
      values: [
        {
          id: 'c3d0b1e2-32d2-4d9c-944f-63e4c9e7b354' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'zzgl. MwSt.',
        },
      ],
    },
  },
  {
    id: 'c6ff2b95-9a58-4591-bb7c-40820a6a27e7' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.shoppingCartPriceLabel,
    text: {
      id: 'f0c16892-1a42-497e-8421-fc55c9699629' as TextId,
      values: [
        {
          id: 'd4e7ab84-bf0c-4d45-b947-58e4413a1a6c' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Gesamtpreis',
        },
      ],
    },
  },
  {
    id: 'f3cc1536-b2df-4e53-8df3-912d1eb6d070' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.shoppingCartTicketLabel,
    text: {
      id: '59c13a93-dc89-47d4-9b04-d6e2383a73c5' as TextId,
      values: [
        {
          id: '7ae24f51-cd3f-4a97-b6d1-49f8b2c6d1d3' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Tickets',
        },
      ],
    },
  },
  {
    id: 'c3944b54-f7c5-4c62-9b6a-53c9c9b38154' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.shoppingCartButtonContinueLabel,
    text: {
      id: 'c27bfe4a-5a3f-4d49-823f-f18a9a4c35db' as TextId,
      values: [
        {
          id: 'd6a0df2a-6c71-4d1c-9d5a-4a92b87980a3' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Weiter',
        },
      ],
    },
  },
  {
    id: 'a60dc938-97c4-4c54-b96b-44217425ed4b' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.shoppingCartButtonAlternativeLabel,
    text: {
      id: 'd20a7c2f-586a-47f5-9fa5-0b1940a5732e' as TextId,
      values: [
        {
          id: 'ca511f45-2e1d-4bfc-8bb3-47999b8b8298' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Zum Warenkorb hinzufügen',
        },
      ],
    },
  },
  {
    id: 'b21459c2-1a9e-4f15-8882-60b0b281fe56' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.shoppingCartButtonCompleteLabel,
    text: {
      id: '8a438c6c-62eb-4d85-8f93-1d8a6f0a9e33' as TextId,
      values: [
        {
          id: 'e7e1ef78-6cb4-4b1f-b1c8-3d1f412ea4b6' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Zahlungspflichtig bestellen',
        },
      ],
    },
  },
  {
    id: 'fc7f6c38-8d22-4ad2-94c1-c3d083748e3c' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.removePaymentMethod,
    relationType: ERelationType.headline,
    text: {
      id: '67f2aed9-bf57-44d0-ac61-1e8ea0618fe2' as TextId,
      values: [
        {
          id: 'f9435551-ff68-4d36-b328-2cc778fb2ceb' as TextLanguageValueId,
          value: 'Zahlungsmethode löschen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '9a69c318-5056-46b0-bc39-6ccc840eb31c' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.removePaymentMethod,
    relationType: ERelationType.description,
    text: {
      id: '99816691-79ca-4fe7-b853-85220784aa74' as TextId,
      values: [
        {
          id: 'd29a4e39-87b9-48de-a008-2395c2b839cd' as TextLanguageValueId,
          value: 'Möchten Sie diese Zahlungsart wirklich löschen?',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '78722437-eb16-4d92-810f-72afd8120ade' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.removePaymentMethod,
    relationType: ERelationType.remove,
    text: {
      id: '55268407-2269-4c60-bc12-6249e0b65d61' as TextId,
      values: [
        {
          id: '69b36535-69fe-4e83-815e-c88d31f9a1c2' as TextLanguageValueId,
          value: 'Löschen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '855b1bff-027e-46ac-9572-03e17fa07af7' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.choosePayment,
    text: {
      id: 'f93587bb-a570-4423-9cc8-6ed49a305e9b' as TextId,
      values: [
        {
          id: 'aa5fe8a9-ada9-4e52-87ca-8caacaf67143' as TextLanguageValueId,
          value: 'Zahlungsmethode auswählen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '2a66a9a1-3ce2-4497-b33c-76b7d61c0b55' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.emptyCart,
    text: {
      id: 'cf813dc7-2232-4877-a704-831fe6fcf2d1' as TextId,
      values: [
        {
          id: 'b2ee8301-e665-4a35-9242-8a0ec12866e4' as TextLanguageValueId,
          value: 'Warenkorb leeren',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'a2b9dfbb-d07c-4cbe-b02d-d5a8c50ae76d' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.removeTicket,
    text: {
      id: '315d1979-8651-40bf-98c7-6c1129cc4027' as TextId,
      values: [
        {
          id: '2a66a9a1-3ce2-4497-b33c-76b7d61c0b55' as TextLanguageValueId,
          value: 'Ticket entfernen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'a6f9f5cb-7354-41c5-9a0d-147447efc210' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.removeTicketFromCart,
    text: {
      id: 'ddc5a756-8fe4-45b1-9dde-6639cf47d4b9' as TextId,
      values: [
        {
          id: '1b95d773-236d-46f5-bad6-93b537b69ed9' as TextLanguageValueId,
          value: 'Das Ticket aus dem Warenkorb entfernen?',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '5abe5e0c-0325-4fef-9e1d-1f97a7fec62f' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.totalPrice,
    text: {
      id: 'b9f53b74-dbe3-46ca-87ca-53678de87090' as TextId,
      values: [
        {
          id: '2eb13a65-75a2-4a0d-bf7b-923aef669eb8' as TextLanguageValueId,
          value: 'Gesamtpreis',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '9be3fa65-83ff-463b-a1f9-e7086bcd4f98' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.tickets,
    text: {
      id: 'bfdd99ef-bfba-4d7e-9c66-65252f2c7651' as TextId,
      values: [
        {
          id: 'bfab95bd-5086-4529-b901-f4cda3f39c48' as TextLanguageValueId,
          value: 'Tickets',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },

  {
    id: '43daea9e-a978-42ef-869d-83dcf2eb6f8a' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.subscriptionShoppingCartLabel,
    text: {
      id: 'cada127f-6cfb-473c-b4ee-a7b6f12faefe' as TextId,
      values: [
        {
          id: '2c82bf7f-5c31-4a14-858e-0a0161f5c382' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Abonnement',
        },
      ],
    },
  },
  {
    id: '1e37778a-7924-4780-81d5-e2ab49e3ca12' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.subscriptionShoppingCartHint,
    text: {
      id: '74065bbd-ed61-4d21-89b4-3570e84b9d1a' as TextId,
      values: [
        {
          id: 'a9ea9ac1-1b54-44e2-81ff-905506945128' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value:
            'Der Gesamtpreis wird nach Erfassung aller Daten des Abonnements an der Kasse berechnet.',
        },
      ],
    },
  },
  {
    id: '5b93e10d-34a9-4dd2-97a4-cffa81b20c7d' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.shoppingCart,
    relationType: ERelationType.maxAmountExceeded,
    text: {
      id: 'bc303031-9d83-4e29-8d04-3e2aa494eed4' as TextId,
      values: [
        {
          id: 'd4f05567-fd2e-40b1-aab8-037ffe8ca4eb' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value:
            'Es können nur maximal {{maxAmount}} Tickets gleichzeitig gekauft werden.',
        },
      ],
    },
  },
];

const DEFAULT_WEB_GENERAL_AREA_TEXTS: INormalText[] = [
  {
    id: '8624863e-4940-41c7-a051-f4d3ae6c1d27' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.dateInputError,
    text: {
      id: '9c43374a-d5a4-4052-b6c8-d12d9c2e5c0d' as TextId,
      values: [
        {
          id: '9aefaf53-9491-4e6e-8922-6b9b485d376a' as TextLanguageValueId,
          value: 'Datumseingabe ungültig',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '852e809c-33b8-4f17-bc1d-f0bd8ec7ab82' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.dateTimeInputError,
    text: {
      id: '523eabbf-36da-47ce-9666-8665b8ba18bd' as TextId,
      values: [
        {
          id: '27c70c10-092a-4b5d-96e4-6ef5d59e8081' as TextLanguageValueId,
          value: 'Datum und Uhrzeit ungültig',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '644d5d23-3baf-478e-be66-004c51f9c2c6' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.timeInputError,
    text: {
      id: 'd7aaa0ed-c62f-4b18-b1b5-1e64abd30847' as TextId,
      values: [
        {
          id: '7ac977a2-f15b-460e-82f7-97a1b254fe87' as TextLanguageValueId,
          value: 'Uhrzeiteingabe ungültig',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'addeecb4-d383-44ad-9ce4-53c175491f36' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.warning,
    text: {
      id: '2402c3c0-23e7-417a-a6c6-88e301755fd2' as TextId,
      values: [
        {
          id: '2fd76d80-0279-4210-ba2a-c4a7608fc8b4' as TextLanguageValueId,
          value:
            'Die Registrierung ist fehlgeschlagen. Die eingegebene Adresse ist ungültig. Bitte geben Sie eine valide Adresse an.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '5fae07e5-0f3b-4cc7-96c9-ade6ac1db52f' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.postalCodeError,
    text: {
      id: 'aa500bfa-5f9d-43a4-9187-ce4a0227ce54' as TextId,
      values: [
        {
          id: 'd15b4632-4fce-4771-89ef-96b53a7cb2f5' as TextLanguageValueId,
          value: 'Das eingegebene PLZ-Format ist ungültig.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '95b23ee3-3fa8-4c91-af02-a326b0968c42' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.confirmation,
    text: {
      id: 'b8e72658-3580-4229-acc2-93ba2df3cd67' as TextId,
      values: [
        {
          id: 'd7eae19c-2dbb-432b-a837-8a5da66d5e6a' as TextLanguageValueId,
          value:
            'Ich bestätige hiermit, dass meine Adresse korrekt ist und möchte die Registrierung mit dieser Adresse fortsetzen.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '1a71d4e2-c091-4a88-b942-2a4594dc01a2' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.accountDataEmail,
    text: {
      id: 'f781d0f0-335a-4e1a-8beb-4228e70cead8' as TextId,
      lastEditedAt: '2025-12-02T13:26:41.479Z',
      values: [
        {
          id: 'ad56e084-646b-4d5b-86ed-d6b83f01371c' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'E-Mail-Adresse',
        },
      ],
    },
  },
  {
    id: '1181a4fc-8972-46e3-b022-fbabbb75351a' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.personalDataHeading,
    text: {
      id: '6dba2a77-3164-46be-ad35-7fca879fbeca' as TextId,
      lastEditedAt: '2025-12-02T13:26:41.479Z',
      values: [
        {
          id: 'e5439578-9706-4251-80e4-cfdc4588cd2e' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Personenbezogene Daten',
        },
      ],
    },
  },
  {
    id: 'b049277c-db89-4431-9783-b19a52ef193b' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.personalDataBirthdate,
    text: {
      id: '20677170-a23e-4e0e-a83c-aeadaa1fbd9a' as TextId,
      lastEditedAt: '2025-12-02T13:26:41.479Z',
      values: [
        {
          id: 'bf77871f-a768-47d2-8adf-f670f90f9a91' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Geburtsdatum',
        },
      ],
    },
  },
  {
    id: 'db5aa0ab-d57b-47c6-9a49-8ca27bf2f3e0' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.newCustomerSalutation,
    text: {
      id: '5d95eb77-59c1-47e3-a886-7330c1e77adf' as TextId,
      lastEditedAt: '2025-12-02T13:26:41.479Z',
      values: [
        {
          id: '84d75499-3fbb-4c3c-a2f4-11b81888b6e6' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Anrede',
        },
      ],
    },
  },
  {
    id: '5f2f5945-b35c-4e1b-b57a-9f73424d6c08' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.newCustomerFirstName,
    text: {
      id: '41c510cf-f35a-472e-9e29-505ddde5f5d3' as TextId,
      lastEditedAt: '2025-12-02T13:26:41.479Z',
      values: [
        {
          id: 'f5d74786-e178-483c-89b2-92da23d92f99' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Vorname',
        },
      ],
    },
  },
  {
    id: 'fc1ea278-23c7-4d35-8a67-30fc3ddbaa36' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.newCustomerLastName,
    text: {
      id: 'ea83d225-c9b9-4fb8-b997-91b698f76ea3' as TextId,
      lastEditedAt: '2025-12-02T13:26:41.479Z',
      values: [
        {
          id: '5d6d9f81-1c3e-4b5a-8943-d68019171199' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Nachname',
        },
      ],
    },
  },
  {
    id: '9d6ff680-81c4-4cf4-915e-9715d94fc910' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.newCustomerTelephone,
    text: {
      id: '1bc28f8b-dc02-48fd-9098-4e9bc2494973' as TextId,
      lastEditedAt: '2025-12-02T13:26:41.479Z',
      values: [
        {
          id: 'd9c13d8c-3437-41d5-b42d-0e5ff68fb0f5' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Telefon',
        },
      ],
    },
  },
  {
    id: '52bb6c98-22c4-46ad-8c6e-ba819295cdc0' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.email,
    text: {
      id: '55286495-674a-44cc-9685-a836fccde5fd' as TextId,
      lastEditedAt: '2025-12-02T13:26:41.479Z',
      values: [
        {
          id: '02fc4ba0-118d-4733-92fb-d09f01eca70f' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'E-Mail',
        },
      ],
    },
  },
  {
    id: 'fd78f730-c3d5-4b92-bbbb-d49cf475724f' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.existingCustomerCustomerNumber,
    text: {
      id: '2502a35a-bf3f-45a3-890f-3643e73c8eea' as TextId,
      lastEditedAt: '2025-12-02T13:26:41.479Z',
      values: [
        {
          id: '861323d4-4421-4e88-82d0-7a33995d4781' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Kundennummer',
        },
      ],
    },
  },
  {
    id: '4f6c8824-0935-49db-b448-6412355d5e31' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.addressDataHeading,
    text: {
      id: '63d27e8e-a0c6-4d86-8202-143f2e64a9ac' as TextId,
      lastEditedAt: '2025-12-02T13:26:41.479Z',
      values: [
        {
          id: 'ecb5cdc9-6430-4210-a4d4-a44e22c6cbe5' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Adressdaten',
        },
      ],
    },
  },
  {
    id: 'd485008d-8c89-49b3-93f6-3a31886513eb' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.addressDataCountry,
    text: {
      id: 'a17946b1-1346-4f26-ab29-c4055dfe4802' as TextId,
      lastEditedAt: '2025-12-02T13:26:41.479Z',
      values: [
        {
          id: 'e1e1c658-4eab-41ad-b779-637bddff7f67' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Land',
        },
      ],
    },
  },
  {
    id: 'd15e8b4a-336e-4b75-896a-75f19d84c635' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.addressDataPostalCode,
    text: {
      id: 'c557e0d9-1a9a-4a98-950e-7070dbf0ad78' as TextId,
      lastEditedAt: '2025-12-02T13:26:41.479Z',
      values: [
        {
          id: '67cf89f0-eafc-486c-bae1-6d370306eb75' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'PLZ',
        },
      ],
    },
  },
  {
    id: 'bb39105a-9d94-40b5-b476-cd9ca2754803' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.addressDataCity,
    text: {
      id: 'f9d61596-47c0-4fe0-84be-834b4f61501c' as TextId,
      lastEditedAt: '2025-12-02T13:26:41.479Z',
      values: [
        {
          id: 'd6659a54-4c0a-472c-94f6-1d9485bf3882' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Stadt',
        },
      ],
    },
  },
  {
    id: 'd46478ae-9ced-46e0-ba28-c5ab0324c04b' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.addressDataStreet,
    text: {
      id: 'ec262092-9f3e-46a4-9412-06448ef58590' as TextId,
      lastEditedAt: '2025-12-02T13:26:41.479Z',
      values: [
        {
          id: 'e0a27189-e0e4-41ac-a4d4-f7cec40a38ef' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Straße',
        },
      ],
    },
  },
  {
    id: 'df8fc562-c680-4777-9a62-d5f313c648cc' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.addressDataHouseNumber,
    text: {
      id: 'a4ae5d16-7e7c-48b9-97ec-bc68b388b867' as TextId,
      lastEditedAt: '2025-12-02T13:26:41.479Z',
      values: [
        {
          id: '414efaba-4646-4daf-a1dc-86379fe5b507' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Nr.',
        },
      ],
    },
  },
  {
    id: '0ca54793-d6bb-418d-9294-49b12d0e8480' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.addressDataExtra,
    text: {
      id: '391250cf-27f4-41f4-bd2f-208d341b5b11' as TextId,
      lastEditedAt: '2025-12-02T13:26:41.479Z',
      values: [
        {
          id: '2c8a00a3-2a7f-4ef8-9038-099203f5e97b' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Adresszusatz',
        },
      ],
    },
  },
  {
    id: '2800a71c-1c86-4971-a88b-e4476069c190' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generalArea,
    relationType: ERelationType.loadPassengerData,
    text: {
      id: '59fb1fe0-1ce4-46b7-8d6b-16e2d3711552' as TextId,
      values: [
        {
          id: '5185d986-6263-4a23-a24b-1ca9655267ae' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Fahrgastdaten laden',
        },
      ],
    },
  },
];

export const DEFAULT_WEB_TEXTS: INormalText[] = [
  ...DEFAULT_WEB_HEADER_TEXTS,
  ...DEFAULT_WEB_BANNER_TEXTS,
  ...DEFAULT_WEB_FOOTER_TEXTS,
  ...DEFAULT_WEB_TICKET_TEXTS,
  ...DEFAULT_WEB_TICKET_CONFIGURATION_TEXTS,
  ...DEFAULT_WEB_DIRECT_TICKET_TEXTS,
  ...DEFAULT_WEB_SHOPPING_CART_TEXTS,
  ...DEFAULT_WEB_GENERAL_AREA_TEXTS,
  {
    id: '1af91ee3-3ff6-44a7-8386-591e59a55952' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.registration,
    relationType: ERelationType.onboardingTitle,
    text: {
      id: '0fa8d077-9bdb-40d0-883d-16a8ca611673' as TextId,
      values: [
        {
          id: 'ae2d356d-d6c3-43e2-a005-6363a77b7c05' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Registrierung abschließen',
        },
      ],
    },
  },
  {
    id: 'ffa9e08d-9867-4f86-8f52-d7141d4ff6c5' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.registration,
    relationType: ERelationType.onboardingCompleteButton,
    text: {
      id: '8eb8dc4c-7537-45ac-aab0-f065972fdd19' as TextId,
      values: [
        {
          id: '0efd1ac7-ce38-4c3b-aeba-f30688272f43' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Registrierung abschließen',
        },
      ],
    },
  },
  {
    id: 'e9907a01-7e6a-46de-8682-ba8f2331ce4a' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.privateAreaLabel,
    text: {
      id: '1206cbdf-2494-4f81-a734-61311be907dd' as TextId,
      values: [
        {
          id: '945ab86a-6d1c-49c2-ba09-d54baf27c786' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Mein Bereich',
        },
      ],
    },
  },
  {
    id: '0df92bd3-c55c-401c-9cff-58f8eadeb0a7' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.accountName,
    text: {
      id: 'bb4d7488-f072-4c33-9867-1c08e8c97f91' as TextId,
      values: [
        {
          id: '4b77ee4d-f527-4594-b8f7-d5dd29b90aa7' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Konto',
        },
      ],
    },
  },
  {
    id: '35e3d278-6dd8-4a57-8b3b-f95a2f261b00' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.userInfo,
    text: {
      id: '84642b74-7d55-44eb-bad5-7e9ec00e73e8' as TextId,
      values: [
        {
          id: 'd5fef6b4-b2db-4e89-be23-f2854e4e5d73' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Benutzerinformationen',
        },
      ],
    },
  },
  {
    id: '3268c0e9-14e5-41a1-adc8-b46eea1a6dcf' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.accountIcon,
    text: {
      id: '8e35bb0a-ede3-451c-b3cf-b026e42aca60' as TextId,
      values: [
        {
          id: '44148a2d-abdc-4403-91ad-313b02465f1a' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'person',
        },
      ],
    },
  },
  {
    id: '8d1e054c-b856-4115-b9d2-227aa1b100d7' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.descriptionText,
    text: {
      id: 'fb9026f9-4bc5-42e1-9249-29bbb88bc5c8' as TextId,
      values: [
        {
          id: '5c89c2d7-f086-40e2-8b72-034dc60e22c1' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value:
            'Ihr Foto und Ihre persönlichen Daten benötigen wir für Ihre Identitätsbestätigung und eine sichere Zahlung. Dies schützt vor Missbrauch und sorgt für eine reibungslose Ticketnutzung.',
        },
      ],
    },
  },
  {
    id: '3dfedb89-58a0-4045-9ae7-1c86f3a84453' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.headlinePhoto,
    text: {
      id: '57f27df6-f5b7-4b79-919a-fc4f7db499b3' as TextId,
      values: [
        {
          id: 'a3bdfab5-6e8f-4f92-a6e3-89e9ab5e5c07' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Ihr Foto',
        },
      ],
    },
  },
  {
    id: 'b0512c95-4534-4c5c-8122-3c1cc692b9aa' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.descriptionPhotoText,
    text: {
      id: '4e8e023f-7fc7-41ee-a047-917ad1b145c2' as TextId,
      values: [
        {
          id: 'f4f2898b-cb5c-42b9-9e92-3a5b0a5114b4' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value:
            'Bitte laden Sie ein aktuelles, gut erkennbares Foto hoch, auf dem Ihr Gesicht deutlich zu sehen ist.',
        },
      ],
    },
  },
  {
    id: '29d3b715-e36e-4b35-9065-b7fb06dfeea7' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.uploadButtonText,
    text: {
      id: 'f2b124d2-1066-41dd-b9a2-62b947eb7c76' as TextId,
      values: [
        {
          id: 'df9d8c7b-2693-421e-90aa-2f7f8e51b54f' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Bild hochladen',
        },
      ],
    },
  },
  {
    id: 'fc3da632-dba5-4442-a5c3-e0a1f08f3ea2' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.deleteButtonText,
    text: {
      id: '39df6ae5-d6db-41a2-916b-b5a177df1654' as TextId,
      values: [
        {
          id: 'fa76b90c-2193-4d3a-bbf8-3705ec251a4c' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Bild löschen',
        },
      ],
    },
  },
  {
    id: '48e2fc03-4e50-4484-bd60-9f32d37d6d17' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: 'saveButtonText',
    text: {
      id: '0903ff1f-bf9a-41df-b5c5-b45c02013d3d' as TextId,
      values: [
        {
          id: '7b1ad179-e23a-4be9-826f-e529f7c173c0' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Speichern',
        },
      ],
    },
  },
  {
    id: '9f8f9f18-3af8-47a5-9d61-7e1b21c61b73' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.paymentInfoLabel,
    text: {
      id: '44423b08-9ae9-436a-a12d-54d0b1e1cf52' as TextId,
      values: [
        {
          id: 'f4c73402-f9bb-45a7-8b02-572905add6ed' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Zahlungsarten',
        },
      ],
    },
  },
  {
    id: 'c9176b90-84ae-460c-8c60-8dd4b83dcd42' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.paymentInfoIcon,
    text: {
      id: '41eaf4ec-2be1-4598-b28a-5d18d1f7c650' as TextId,
      values: [
        {
          id: 'f08d8f82-dfd0-4bb0-a622-748310f9810d' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'credit_card',
        },
      ],
    },
  },
  {
    id: '6f6b5e14-6462-4ec6-b2d7-16b7e5b9c939' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.directTicketsDescriptionText,
    text: {
      id: '4b3cddaa-b83c-42e0-91b0-3180f4ed6b19' as TextId,
      values: [
        {
          id: 'b1b67a32-1d2b-4ac1-b6ea-c9cfe3d5d6db' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value:
            'Die gewählte Zahlungsart wird als Standard für zukünftige Zahlungen von Direkt-Tickets verwendet.',
        },
      ],
    },
  },
  {
    id: '9b8419fb-bcdb-4087-8e7e-7cd94a857ea0' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.directTicketsLabel,
    text: {
      id: '1d5e2c53-96b3-4a94-9a47-3af09710907d' as TextId,
      values: [
        {
          id: '84fbdabc-624e-44f4-bb0a-4c8aa9b22510' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Für Direkt-Tickets',
        },
      ],
    },
  },
  {
    id: 'ec0b7ee7-74f3-40d2-8288-e6c62ef758f3' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.paymentInfoDirectAddLabel,
    text: {
      id: '26f0a0df-9f1b-44c7-a601-6617f1b228e7' as TextId,
      values: [
        {
          id: 'a1e429d8-ffb4-4ec8-80d1-b810fe0f8f21' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Neue Zahlungsart in LOGPAY hinzufügen',
        },
      ],
    },
  },
  {
    id: 'd04056e9-66f5-47c7-82af-1a4f3c39d289' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.aboTicketsLabel,
    text: {
      id: '23e9b6ac-5f94-4826-9672-b74c663de54a' as TextId,
      values: [
        {
          id: '8e4508da-f4b3-4bdb-a16d-129b7c29328e' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Für Abonnements',
        },
      ],
    },
  },
  {
    id: 'fb9a8d04-270c-4d8f-8903-f0e0a4f75b5d' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.aboTicketsDescriptionText,
    text: {
      id: '8cf8411f-7e32-4635-b06f-d4e8ffb6e6db' as TextId,
      values: [
        {
          id: 'aab31254-e290-42f2-b3ef-bbf57491b6db' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value:
            'Die gewählte Zahlungsart wird als Standard für zukünftige Zahlungen von Abonnements verwendet.',
        },
      ],
    },
  },
  {
    id: '781f9c34-2392-4632-9ed1-4b365cae6f22' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.paymentInfoAboAddLabel,
    text: {
      id: '41b5f223-8b0c-4d64-9126-9f84359352cf' as TextId,
      values: [
        {
          id: '06e087d4-0f45-4f32-b779-13fcf7a2e931' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Neue Bankverbindung hinzufügen',
        },
      ],
    },
  },
  {
    id: 'f94c2a25-0b55-480f-9e69-02c1a6bca60c' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.deleteAccountLabel,
    text: {
      id: 'cf690d64-bc2c-4ff7-9c92-0dfdc1c21ec8' as TextId,
      values: [
        {
          id: '3b85efb2-2a65-4a1d-a58c-9c0af1f72f6c' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Konto löschen',
        },
      ],
    },
  },
  {
    id: '33c56c5b-6f5c-476f-9857-ff1608e0651f' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.deleteAccountDescriptionLabel,
    text: {
      id: '24faeb1b-13b1-4600-9a7f-524c47ae32ae' as TextId,
      values: [
        {
          id: '9f9b3f18-1f79-4ed4-8850-3ccab8ff9b3f' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value:
            'Ihr Konto kann nach einer Löschung nicht wiederhergestellt werden.',
        },
      ],
    },
  },
  {
    id: 'd38b6f32-5db9-47d5-9aa0-0b23f4f3445f' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.deleteAccountButtonLabel,
    text: {
      id: 'e2f6cc3f-960b-42a9-88c0-df12351a7d25' as TextId,
      values: [
        {
          id: '25a42c23-5c21-4b98-9a8d-94b4a1d9e0b8' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Konto unwiderruflich löschen',
        },
      ],
    },
  },
  {
    id: '70f95cbd-3fe9-46b5-adbb-70a2a733ddef' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.paymentInfoDirectAddDisabled,
    text: {
      id: '01298c1a-09ef-4ed7-b15c-04cb7e8aeb74' as TextId,
      values: [
        {
          id: 'f2cc09b8-5e10-4163-88c9-d4094f38304f' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Neue Zahlungsart kann nicht hinzugefügt werden',
        },
      ],
    },
  },
  {
    id: '384a2811-a9b4-4e68-96e9-94882936638e' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.saveButtonText,
    text: {
      id: '7e3b4205-569a-4888-9a75-d2cee489001d' as TextId,
      values: [
        {
          id: 'c658cae3-4fae-436e-88d1-391c3488f3b8' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Kann nicht gespeichert werden',
        },
      ],
    },
  },
  {
    id: '40322abc-32c5-4398-acf5-4643117ec351' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.loading,
    text: {
      id: 'b04d0134-42e7-4ec3-945a-1e2ae7b590b9' as TextId,
      values: [
        {
          id: 'e262ee87-ca78-4fab-93ed-c7b973602afb' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Benutzerinformationen werden geladen.',
        },
      ],
    },
  },
  {
    id: '33423a88-89ed-49f4-a74e-20f23aa5f56b' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.back,
    text: {
      id: '5d98208c-b8eb-4d02-a809-7374e52f7f9d' as TextId,
      values: [
        {
          id: '72037188-35ac-4f0b-aa1a-17854e16b7c8' as TextLanguageValueId,
          value: 'Zurück',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '5da65e6c-2646-4b3b-8629-1d913126ecf3' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.continue,
    text: {
      id: '7cdc4a10-308d-46fd-a591-d110a7a26076' as TextId,
      values: [
        {
          id: '50bef35d-bdee-4389-b283-a347509e9387' as TextLanguageValueId,
          value: 'Weiter',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '46de5f9a-c400-4b8d-a99c-3fe5b1500e83' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.cancel,
    text: {
      id: '778453b7-5b14-49c6-a623-a61f579d30ed' as TextId,
      values: [
        {
          id: '468dfbe4-ca4a-4064-9b5d-339c9c937602' as TextLanguageValueId,
          value: 'Abbrechen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '8d34bb8d-01bb-4392-a94f-086063906fae' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.save,
    text: {
      id: 'e691159a-2aab-417b-abd2-59e7c0e15fef' as TextId,
      values: [
        {
          id: '7f356646-1416-46fb-b810-5ba543ea31a5' as TextLanguageValueId,
          value: 'Speichern',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '662882b8-d2e3-48fd-a37e-feaeab05b594' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.edit,
    text: {
      id: 'e59e374d-3f56-40db-9aee-8cd1a726e57e' as TextId,
      values: [
        {
          id: 'be84edbd-7128-449c-a898-e5b78f3066d0' as TextLanguageValueId,
          value: 'Bearbeiten',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '4a5f5c73-c6c3-4daf-b336-66765b5664f5' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.personalData,
    text: {
      id: '8f2eeb1a-2118-4f9c-b8fe-052bf7b0a81a' as TextId,
      values: [
        {
          id: '3820f23f-58a4-4970-bb9c-35f553d4a6e2' as TextLanguageValueId,
          value: 'Persönliche Angaben',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '1481f7ac-511f-4f38-b609-438c92d3c974' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.backToShop,
    text: {
      id: 'ad0c6b05-6d92-43e1-a533-41c7aae815e9' as TextId,
      values: [
        {
          id: '16d7911c-f9bb-4a67-b385-eca49ea561d0' as TextLanguageValueId,
          value: 'Zurück zum Shop',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '6a9075f9-b59e-4fc6-9037-e2f6efb94822' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.remove,
    text: {
      id: '3840f481-cf63-482d-a347-567c4aa4e97e' as TextId,
      values: [
        {
          id: 'eb29a7cb-e269-4cdd-8d3c-2e3c84f6ee9d' as TextLanguageValueId,
          value: 'entfernen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'f0467d60-b713-4269-8e0b-8b24a13e9977' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.accessDenied,
    text: {
      id: '35fe6acf-120c-402f-81cb-fcc0a0c1df3c' as TextId,
      values: [
        {
          id: '31522ab2-465c-4d07-b91b-eb44c1bff5fe' as TextLanguageValueId,
          value: 'Zugriff verweigert!',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '81309562-5de5-41cf-83c5-6311cef3058d' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.pageNotFound,
    text: {
      id: '82a4f205-e8df-474d-b29d-128dc654819d' as TextId,
      values: [
        {
          id: '3f59db08-2456-405e-a9be-a9ad3a86f6c5' as TextLanguageValueId,
          value: 'Die angegebene Seite existiert nicht.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'e3c7e65c-a86d-40d7-8a95-17fb99528f84' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.errorPageTitle,
    text: {
      id: '9bae7bdb-0dda-45cf-8d17-89d98ae134c4' as TextId,
      values: [
        {
          id: 'ec4ad721-a0ee-4e3c-ae91-2862364c4e02' as TextLanguageValueId,
          value: 'Fehlerseite',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '5fa9e3e8-b83d-43e1-8761-8a8e0bbcd22f' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.noErrors,
    text: {
      id: 'f5251208-7f75-4972-b9b0-09558da97640' as TextId,
      values: [
        {
          id: 'af0a4041-1caa-460a-9a83-c797891e1ffa' as TextLanguageValueId,
          value: 'Keine Fehler vorhanden.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '9d55ecd7-1952-4c63-ba4a-684c5659973f' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.amount,
    text: {
      id: 'a4133a6c-d4d9-470b-a0fe-f490c2af310c' as TextId,
      values: [
        {
          id: 'a6bd586a-6621-4ed1-b256-23189fdb14a1' as TextLanguageValueId,
          value: 'Anzahl',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '938dd02f-65ae-4004-b24d-7fbe1ffe4a03' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.continueTicketConfig,
    text: {
      id: '2610aedd-f1de-4cb2-9d77-63cfa6b219ac' as TextId,
      values: [
        {
          id: 'e5b1b0c1-df74-46a0-b42c-8e6275ab0e8d' as TextLanguageValueId,
          value: 'Mit Bestellung fortfahren',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd6aaa492-3876-463a-be91-748011f23012' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.addToConfiguration,
    text: {
      id: '24af5d5e-bc9a-437e-96bf-36f6b96668b5' as TextId,
      values: [
        {
          id: '938dd02f-65ae-4004-b24d-7fbe1ffe4a03' as TextLanguageValueId,
          value: 'Weiteres Ticket hinzufügen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '207e8cd8-22d9-43a7-a481-127ed56908d4' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.registryTos,
    text: {
      id: '1ffbb634-bc6d-42ce-b5cb-7730c1f0906e' as TextId,
      values: [
        {
          id: '6287c88b-f7bb-4dec-902f-6384173251cf' as TextLanguageValueId,
          value:
            'Mit der Registrierung erkläre ich mich sowohl mit den Allgemeinen Geschäftsbedingungen als auch mit der Datenschutzerklärung einverstanden.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'f1433087-89ff-4a00-879f-1e4db2e0ba35' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.hasToBeValidEmail,
    text: {
      id: '68f27016-b75b-4a44-b163-590a04d997c2' as TextId,
      values: [
        {
          id: 'd84cfafb-b2ae-4dc0-9bdb-7232b77faf74' as TextLanguageValueId,
          value:
            'Email-Format ungültig. Geben Sie eine valide Email-Adresse ein.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '7c985c53-f898-4bfc-9806-153dffc74ce0' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.info,
    text: {
      id: '0485ac85-5ea5-4554-9513-ac24a16fbef9' as TextId,
      values: [
        {
          id: '8ac66466-ac9c-4ad2-a54f-4287ed173c47' as TextLanguageValueId,
          value: 'Info',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'b1669741-4cfa-4b41-9d63-7342c2384a71' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.close,
    text: {
      id: 'ed61f6c9-0cdb-4904-bf95-bcf8f3c126a2' as TextId,
      values: [
        {
          id: 'e011a59b-4371-4bf4-8fde-5bbff61daffe' as TextLanguageValueId,
          value: 'Schließen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '48d1523d-14ad-4683-8171-123b06c4fdd4' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.aboToggle,
    text: {
      id: 'ddd1c586-fa51-440e-9a72-7748d4778c83' as TextId,
      values: [
        {
          id: '8dae1d69-5acd-4f93-bed8-02cbac778266' as TextLanguageValueId,
          value: 'Abo',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '7ef79392-1a9b-426f-b040-7feeb1649490' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.directToggle,
    text: {
      id: '6f465bae-6678-4599-82a5-b907a3a9bf5d' as TextId,
      values: [
        {
          id: 'eaea4ef6-5dc0-4979-9739-9f9126baa80c' as TextLanguageValueId,
          value: 'Direkt',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '5d9eea4a-5696-41c1-9f4e-66cb48a4867c' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.search,
    text: {
      id: '69f91b9f-fcf9-48d4-8ebb-ba9ef43d46bc' as TextId,
      values: [
        {
          id: '79aadd20-dd67-4aca-bc3c-e415cb26082e' as TextLanguageValueId,
          value: 'Suche',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '8c13ee9e-e864-4356-be7f-5f51821ea3bd' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.relationSearch,
    relationType: ERelationType.origin,
    text: {
      id: '40d64c94-7e3d-4618-a6d6-5aac5cf9cad7' as TextId,
      values: [
        {
          id: 'a8e58707-9bf8-4b42-ac8a-cc39c956339d' as TextLanguageValueId,
          value: 'Start',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '35b99836-1b48-4505-9f08-56c5d3aa9c0e' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.relationSearch,
    relationType: ERelationType.destination,
    text: {
      id: '9bde22aa-bcc2-427e-9424-0915049f3e94' as TextId,
      values: [
        {
          id: 'df6cf8f6-4ccb-471f-81eb-d9b600aea407' as TextLanguageValueId,
          value: 'Ziel',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'dcfa0a69-54d4-4064-8ae9-98ddd985d414' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.relationSearch,
    relationType: ERelationType.via,
    text: {
      id: '1caee60f-f897-49d7-8cf3-8dd9453a05f5' as TextId,
      values: [
        {
          id: '8f407192-391e-41a6-922c-7eb42ebf26cf' as TextLanguageValueId,
          value: 'Via',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'ad3f9d96-4b83-4e38-a4ab-2c7fdc493451' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.relationSearch,
    relationType: ERelationType.startDate,
    text: {
      id: '2d384ef0-8b53-432b-94fd-c13a108dc7b0' as TextId,
      values: [
        {
          id: 'd6b16596-d8a6-419d-b261-3d2dd8cd0a4e' as TextLanguageValueId,
          value: 'Abfahrt',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'fdcb49e3-cf6c-417c-ac8b-45b328a729d0' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.noOptions,
    text: {
      id: '6b4a177d-7d54-402c-a125-ddbaf413ade9' as TextId,
      values: [
        {
          id: 'f3abbbe3-045f-4302-9bfa-49b325f91ff7' as TextLanguageValueId,
          value: 'Keine passenden Ergebnisse...',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'f3705bc4-1730-4795-ae74-5492a9f40abc' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.monday,
    text: {
      id: '995ee63c-a649-4fa6-b2c3-80d2f9d5f51b' as TextId,
      values: [
        {
          id: 'f1b5af9f-9271-465c-a1c8-e4b876c5f9da' as TextLanguageValueId,
          value: 'Montag',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '1105a0c6-53b2-4846-8436-a9880307158d' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.tuesday,
    text: {
      id: '0791b7da-2580-432d-a8de-d4fce9274f04' as TextId,
      values: [
        {
          id: '9e9fe0a4-d766-4d65-82b4-da5370572ba4' as TextLanguageValueId,
          value: 'Dienstag',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'aad3fb30-ffa3-4f94-a64c-d1429ef3b155' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.wednesday,
    text: {
      id: '58db1383-949c-4ac2-9ff6-5b32f74a1ef0' as TextId,
      values: [
        {
          id: '6fa142f3-37ff-4499-9f5f-ce0e1b66a85f' as TextLanguageValueId,
          value: 'Mittwoch',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '5c2ee5f1-e3d9-4f88-b533-73e5e6757803' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.thursday,
    text: {
      id: '47b50fb7-f491-46e3-b4a5-f56c064b425b' as TextId,
      values: [
        {
          id: '249068db-59db-4309-8882-f7aa9a0f4b0b' as TextLanguageValueId,
          value: 'Donnerstag',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '8974d1ff-8c05-486d-be79-9a30c17342d2' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.friday,
    text: {
      id: '34c2c207-e0cc-487d-a5ed-8272ac99168a' as TextId,
      values: [
        {
          id: '8da06686-5457-4dae-ae17-a772a1bf3cf9' as TextLanguageValueId,
          value: 'Freitag',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '9ebe4172-04fc-48a5-b953-f6a60d58dadc' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.saturday,
    text: {
      id: '27aa7939-5438-4775-8d9d-f48b93aefadd' as TextId,
      values: [
        {
          id: '788a20e5-a458-4691-91ed-985b68209180' as TextLanguageValueId,
          value: 'Samstag',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '134f24e5-f8f8-423d-9d1a-2d2ff32e71a3' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.sunday,
    text: {
      id: '8865926c-dc10-4d77-ab3e-fa5efd83c258' as TextId,
      values: [
        {
          id: '577e693b-679d-4388-9baf-bad7d3ee4a8c' as TextLanguageValueId,
          value: 'Sonntag',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '1a53acde-2008-4b21-813b-6d101ab9ce96' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.shortMonday,
    text: {
      id: '7c5d7fed-2131-461f-a2e5-09008ce8513c' as TextId,
      values: [
        {
          id: '4f38d49a-f5d4-4d5a-ab15-9fd4200c5f19' as TextLanguageValueId,
          value: 'M',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '7202abb5-359b-44a1-86a5-63c4a2fce222' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.shortTuesday,
    text: {
      id: '9fcd00e7-0e68-47fb-ab77-2d4bee4d7b69' as TextId,
      values: [
        {
          id: '409addf6-3fa9-4568-bdc9-d8dfb0080792' as TextLanguageValueId,
          value: 'D',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '4fa84452-3fcd-4b12-a051-d5569925374b' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.shortWednesday,
    text: {
      id: 'b5f087bf-c8bf-4152-b113-7c4dce42dc26' as TextId,
      values: [
        {
          id: '96d0c757-8507-4ca2-ad76-9f76db535f68' as TextLanguageValueId,
          value: 'M',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '5ace6e3d-2f17-4056-8552-3e4f7393aae7' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.shortThursday,
    text: {
      id: '4e824bf5-f2ab-4586-a626-63464f79492c' as TextId,
      values: [
        {
          id: '3600bb77-09a5-4a43-9d3c-fdb1d6e57145' as TextLanguageValueId,
          value: 'D',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '2475ff48-152c-4341-80dd-1e9433ad67f2' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.shortFriday,
    text: {
      id: '85218a72-f109-4e19-bb7d-a9b929e40996' as TextId,
      values: [
        {
          id: 'b1e131a0-e85f-4697-b900-fe9b0bfd3b03' as TextLanguageValueId,
          value: 'F',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd9a9d5d5-99ef-4caa-892a-010e72ee5357' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.shortSaturday,
    text: {
      id: '04b9796b-9617-4821-9bf8-d517a87bf16b' as TextId,
      values: [
        {
          id: 'ddbabad8-0d8a-4608-9dba-3dd26ac44cb2' as TextLanguageValueId,
          value: 'S',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '3f4fdd93-9b4c-4891-8422-334491f45756' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.shortSunday,
    text: {
      id: 'dcf74fab-4618-4065-90b4-0785cc5c753d' as TextId,
      values: [
        {
          id: 'eba6947d-645a-495e-8d23-ca81b4137b8c' as TextLanguageValueId,
          value: 'S',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '968a334a-60f9-4474-b604-125517436090' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.january,
    text: {
      id: 'a8729c59-bf2b-412a-950e-99c14bb2b70f' as TextId,
      values: [
        {
          id: 'b6b6aab6-8160-427b-9bae-5863fb0b678b' as TextLanguageValueId,
          value: 'Januar',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '7bf9c2b5-11ef-419c-9b1e-a0ba168b5c25' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.february,
    text: {
      id: '16a84306-63dc-4ef4-935e-2a7fb06269a0' as TextId,
      values: [
        {
          id: '45478125-15cb-48aa-9c09-a727015ede7d' as TextLanguageValueId,
          value: 'Februar',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '46af6968-a649-4b1d-9362-5eda17a4a736' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.march,
    text: {
      id: '408e1782-634a-485a-bbc4-197ae588d2f1' as TextId,
      values: [
        {
          id: 'a4d15560-86b7-4db4-8924-d602af7be591' as TextLanguageValueId,
          value: 'März',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '5168c6f1-be50-4a63-890e-a97b1f4b7c4c' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.april,
    text: {
      id: 'fa3615cd-1a43-403b-a88e-535b9b86f534' as TextId,
      values: [
        {
          id: '1899fa81-862b-4ad1-a529-dbb71e3140dc' as TextLanguageValueId,
          value: 'April',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '9784ceca-c944-4cdf-93ba-e88e6cb809c7' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.may,
    text: {
      id: 'dacf9eb1-6cc7-4375-b562-d93d04080e5b' as TextId,
      values: [
        {
          id: 'f6bb41cd-d198-439c-be00-ea34e3aeea4b' as TextLanguageValueId,
          value: 'Mai',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'f0b5414d-f551-47fc-8bdd-5c85433c12e8' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.june,
    text: {
      id: 'd2955203-0df5-4460-9a9b-b3bf2a6efcd6' as TextId,
      values: [
        {
          id: 'dab62f8d-fa38-4e65-abb6-2836810d36f7' as TextLanguageValueId,
          value: 'Juni',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '9762c2b2-878b-4d1c-a5b1-a960e5aaf7e1' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.july,
    text: {
      id: '60d2047b-dfbd-4735-bd6c-59d963889668' as TextId,
      values: [
        {
          id: '4e1d1d60-2a5a-43ea-afbe-c78fa9f3a913' as TextLanguageValueId,
          value: 'Juli',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '7262c390-29f6-4abd-bc54-e432cd3df99c' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.august,
    text: {
      id: 'd876443f-79e7-4ddd-8311-4ad0f80a9222' as TextId,
      values: [
        {
          id: '1c7e9ec2-4814-47ad-b306-721034db68ae' as TextLanguageValueId,
          value: 'August',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'cce3852c-c8ad-47e5-a9f7-6bf88dc77a36' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.september,
    text: {
      id: 'e095f8e6-774d-4e22-84e8-ccf05e84f7d7' as TextId,
      values: [
        {
          id: 'ad141724-6b6b-472f-885a-c50024e78203' as TextLanguageValueId,
          value: 'September',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'c9b2039d-75fe-4e55-a7c0-de7d980de418' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.october,
    text: {
      id: '7bf16d65-75a2-43c7-8d7f-d4636854b203' as TextId,
      values: [
        {
          id: 'cb7b5417-3199-42c7-a612-ed396f7bdbe3' as TextLanguageValueId,
          value: 'Oktober',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '27a7b331-06c6-40ee-b80e-a8f3fd76048f' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.november,
    text: {
      id: '27caae15-f274-4bfe-b2cc-1b317b867203' as TextId,
      values: [
        {
          id: '286d4894-7974-4d25-aa9a-337aed8c87e4' as TextLanguageValueId,
          value: 'November',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '382a5d32-2bd9-48f9-a40e-98d0ba940ee5' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.december,
    text: {
      id: '860ca8cd-39b5-4ff2-a66e-97bec803ef1a' as TextId,
      values: [
        {
          id: '6f724af9-6b39-4f14-8ebf-d61c85ec672e' as TextLanguageValueId,
          value: 'Dezember',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '2ea941bd-1f23-41ed-9d84-f4faa33d417d' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.now,
    text: {
      id: 'c66f5e0c-82de-49de-bfe6-46dbbd01106b' as TextId,
      values: [
        {
          id: 'c363b297-e345-434f-b40e-f0d1730cbf91' as TextLanguageValueId,
          value: 'Jetzt',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'e38444c8-a1f3-42c4-89d2-be6dcf84841b' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.in15Minutes,
    text: {
      id: '4b98f437-c21e-4da0-82f2-a7361c86a4c6' as TextId,
      values: [
        {
          id: '5a074615-290b-4016-986d-09c2721b38e0' as TextLanguageValueId,
          value: 'In 15 Min.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'ceee97e5-3e40-4fb6-8f7a-349a0c79d869' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.in60Minutes,
    text: {
      id: 'ee5fbbef-9307-45d2-8482-68de08241922' as TextId,
      values: [
        {
          id: 'de13d1c7-fcc9-4e38-8ce8-d8a3bff9c07e' as TextLanguageValueId,
          value: 'In 60 Min.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '24bd306e-afef-4368-bb78-ccd6c6fd8bde' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.departure,
    text: {
      id: 'a998a77a-58a3-478a-acae-7f541bae8f78' as TextId,
      values: [
        {
          id: '0249d614-9af8-465f-b957-c90fc6931b9c' as TextLanguageValueId,
          value: 'Abfahrt',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '0d5c0719-a78a-4e1f-a211-9dd2ec220014' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.arrival,
    text: {
      id: '20a9c8ed-4dd0-4155-950e-1e03c2d0aaf5' as TextId,
      values: [
        {
          id: '6db91f0a-d60b-4f0b-85a1-12364f35f202' as TextLanguageValueId,
          value: 'Ankunft',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '714fb626-9832-4711-bf38-7616a929e954' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.editJourney,
    text: {
      id: '0c8e2e06-a9a6-45cc-9144-5b7df0819f4f' as TextId,
      values: [
        {
          id: '666bd580-e9c4-44f9-a510-0fb4b64c2fea' as TextLanguageValueId,
          value: 'Fahrt bearbeiten',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '70bf9729-eef4-417d-b07b-c5919901fd51' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.from,
    text: {
      id: 'ffab1eff-e5f4-40f7-beb7-236f19b767fa' as TextId,
      values: [
        {
          id: 'fec47cd0-bd0c-4435-a715-0f7a0e89caff' as TextLanguageValueId,
          value: 'ab',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '5ed48402-8ffa-4fc3-b2d3-26fafa040f68' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.timeFrom,
    text: {
      id: 'cfa97c42-3309-4ffa-aff9-a047b6d7b101' as TextId,
      values: [
        {
          id: 'a342fae7-a181-42df-8a1a-6527a047f88b' as TextLanguageValueId,
          value: 'Zeit ab',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'fd97245c-a7f4-47c3-9fcd-914006235b21' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.loginLogout,
    relationType: ERelationType.loginButton,
    text: {
      id: 'c5004cd2-e8d6-48ff-8820-0559d4412fe5' as TextId,
      values: [
        {
          id: '14659e9c-fe2e-467f-a7a7-1c284191f301' as TextLanguageValueId,
          value: 'Anmelden',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '5ec5cb93-31d2-499f-ad3b-95c50a3225b9' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.loginLogout,
    relationType: ERelationType.loginHeader,
    text: {
      id: '78de9e63-9dbe-416c-98c6-0567fb5745cd' as TextId,
      values: [
        {
          id: '86013c9c-e5ca-42d2-9b18-3f8c1b534de3' as TextLanguageValueId,
          value: 'Mit Kundenkonto anmelden',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'f8dff7fe-888d-4d80-915e-de95e50252a5' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.loginLogout,
    relationType: ERelationType.email,
    text: {
      id: '24f29c19-3615-4ac8-a124-d62bc8a08254' as TextId,
      values: [
        {
          id: '6f0d223c-05be-4408-b2fe-8e120fb0e22b' as TextLanguageValueId,
          value: 'E-Mail Adresse',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '00714238-9b71-4350-b2b7-548d3d42ddbf' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.subscription,
    relationType: ERelationType.headline,
    text: {
      id: 'c5b3f77d-d50f-4af6-88dc-5dd201229fb5' as TextId,
      values: [
        {
          id: '9302e311-2e81-43a8-a8bb-07d83c83681d' as TextLanguageValueId,
          value: 'Bankdaten anlegen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '8b6389b6-b876-4a42-9e2c-1afebf3a73c3' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.subscription,
    relationType: ERelationType.firstName,
    text: {
      id: '8c33caeb-439a-43fd-8416-53210c416718' as TextId,
      values: [
        {
          id: '6642bd6b-e4c5-4586-9531-4e9de15ee0d6' as TextLanguageValueId,
          value: 'Vorname',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'e61fc9b7-9c66-4fe9-bae9-bd6c418d5891' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.subscription,
    relationType: ERelationType.lastName,
    text: {
      id: 'e17e9ed9-f5e8-4afc-bcde-05e5fe6dac5b' as TextId,
      values: [
        {
          id: '86b45d45-2369-4b85-8541-e034de8998ba' as TextLanguageValueId,
          value: 'Nachname',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'cda640e8-ccc8-43f0-a272-02494c0c2024' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.subscription,
    relationType: ERelationType.iban,
    text: {
      id: '41c06f1c-a38a-401e-9dd1-e5dcfd0015b0' as TextId,
      values: [
        {
          id: 'fd105116-7406-481f-9102-cf49bf14aab2' as TextLanguageValueId,
          value: 'IBAN',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '0f2d9876-73d8-4be4-b999-8dff0b499404' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.subscription,
    relationType: ERelationType.bic,
    text: {
      id: 'b7887000-4c6e-4158-b47f-5aaa402dd218' as TextId,
      values: [
        {
          id: '438acd32-4e47-4737-85c7-12760e4edfb3' as TextLanguageValueId,
          value: 'BIC',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '4e401909-5ad0-4336-9c2c-d1a967aa2fbb' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.subscription,
    relationType: ERelationType.creditInstitute,
    text: {
      id: '1dfaad92-2542-4f46-b067-8cbb0c876123' as TextId,
      values: [
        {
          id: '9369a338-f6ba-4d62-b4f4-feb519425713' as TextLanguageValueId,
          value: 'Kreditinstitut',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '474a83c1-b991-4a1f-bf7d-a607e4ca952d' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.subscription,
    relationType: ERelationType.sepaAgreement,
    text: {
      id: '0f1b3460-6ceb-4eaf-a37d-9deae821e413' as TextId,
      values: [
        {
          id: '4e821b47-cb9c-4ae4-a76f-d44722dfd44a' as TextLanguageValueId,
          value:
            'Ich erkläre mit der Erteilung des SEPA-Lastschriftmandates, dass ich Inhaber:in bzw. Verfügungsberechtige:r des oben angegebenen Kontos bin und der {{shopOwner}} widerruflich ermächtige, das jeweilige tarifliche Fahrgeld und die sonstigen Beiträge von meinem Konto monatlich im Lastschrifteinzugsverfahren einzuziehen. Ich weise meinen mein Kreditinstitut an, die vom Gläubiger {{shopOwner}} gezogenen Lastschriften einzulösen. Ich hafte gemeinsam mit dem Abonnenten als Gesamtschuldner für die Einhaltung aller Verpflichtungen aus dem Abonnentenvertrag. Mit den Abo-Bedingungen, die in den Tarifsbestimmungen geregelt sind, habe ich mich vertraut gemacht und erkenne diese an. ',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '6ca5d874-3fe6-46e4-9c36-725139bc253a' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.client,
    text: {
      id: '3af39c05-cd28-4b0d-b96d-9e3748b61218' as TextId,
      values: [
        {
          id: 'bb9cb3c8-5ebd-4cb3-878f-d5ea6670a17d' as TextLanguageValueId,
          value: 'Amcon Testbetrieb',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'da06885d-3ac6-4d30-a12c-155be681b243' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.dateFormat,
    text: {
      id: '640334a7-ecb9-4b2a-8c18-d90e97adaeaf' as TextId,
      values: [
        {
          id: 'b4d29827-4f6c-441e-84a4-e96f50443107' as TextLanguageValueId,
          value: 'dd.MM.yyyy',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '48a3d918-8210-49da-98c8-c1bbfd03d4cb' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.timeFormat,
    text: {
      id: 'c920dcba-35b4-408e-9486-a895b95d27c0' as TextId,
      values: [
        {
          id: 'bcec78a9-c5a1-4185-9160-4487aed151bf' as TextLanguageValueId,
          value: 'HH:mm',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },

  {
    id: '131bc199-6980-4116-8a07-f219ef911c20' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.journeyPlanner,
    relationType: ERelationType.headline,
    text: {
      id: '38bd518b-a6dc-4e80-a1f5-499cf5487ebe' as TextId,
      values: [
        {
          id: '585f5835-8b0d-42bf-a7ff-fc018247bd46' as TextLanguageValueId,
          value: 'Zur Fahrplanauskunft',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '4828dbd6-8b41-4488-ab0b-e50f594251bd' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.journeyPlanner,
    relationType: ERelationType.button,
    text: {
      id: '7f4c40f7-d5eb-4f69-916d-4785d3d4f4f9' as TextId,
      values: [
        {
          id: 'a7fb8491-24b9-4fbd-b080-c381e914480b' as TextLanguageValueId,
          value: 'Suchen Sie jetzt Ihre passende Verbindung!',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '3cb944df-51f0-4815-b294-3755328a74af' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.journeyPlanner,
    relationType: ERelationType.date,
    text: {
      id: '526d0e18-67e5-4b6f-a0a1-7e8d877d8721' as TextId,
      values: [
        {
          id: '9f5c025e-306a-4359-a2e1-712b092699ab' as TextLanguageValueId,
          value: 'Reisedatum',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'a9379e80-acc6-4119-8d83-27a4d11fb8fe' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.journeyPlanner,
    relationType: ERelationType.date,
    text: {
      id: 'e986cbfc-c226-4230-8bce-47761db487e7' as TextId,
      values: [
        {
          id: '019b7ec4-2abf-4d82-ae3c-1977081f3a5c' as TextLanguageValueId,
          value: 'Reisedatum',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },

  {
    id: '3dc6fd40-0749-4897-8fad-e98bbecff8d0' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.journeyPlanner,
    relationType: ERelationType.from,
    text: {
      id: '4afe975c-6691-44de-affc-50117bbd5709' as TextId,
      values: [
        {
          id: 'd5f10b77-3ba3-4f39-a952-786cefac16d7' as TextLanguageValueId,
          value: 'Von',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'ed33561e-96e7-445f-8dc9-4a889fed10f0' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.journeyPlanner,
    relationType: ERelationType.to,
    text: {
      id: 'd427c95c-7c0c-45ba-a821-d0a8632e94ee' as TextId,
      values: [
        {
          id: 'ed8d822a-6ce7-4adf-9347-00054d26a248' as TextLanguageValueId,
          value: 'Nach',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '15cc1f6a-11f3-44a1-933e-5c2027b675f6' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.ticketCategory,
    relationType: ERelationType.noTicketFoundForCategory,
    text: {
      id: 'eb2cb19f-89df-48b5-b343-9bd96573d92d' as TextId,
      values: [
        {
          id: '4e547a48-7e32-4428-b977-b71ee6c08580' as TextLanguageValueId,
          value: 'Keine Tickets für diese Kategorie gefunden.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '078bd3d3-c018-4906-98d5-612b007f0dd5' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.newCustomerSalutation,
    text: {
      id: '3c06a41a-da01-4ea8-9835-615d2a13c298' as TextId,
      values: [
        {
          id: 'e5e2d6dc-686e-4cdd-abf5-c72c548889d3' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Anrede',
        },
      ],
    },
  },
  {
    id: '2cd45d2a-4f27-4794-921f-64c9afc12134' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.newCustomerFirstName,
    text: {
      id: '0f066f7a-1994-4ebc-a119-e6eb755ba706' as TextId,
      values: [
        {
          id: '919ec000-f323-4654-bae9-4932b8dca6a3' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Vorname',
        },
      ],
    },
  },
  {
    id: 'd79319cf-615b-49e0-b640-11f24887ea72' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.newCustomerLastName,
    text: {
      id: 'ee9926e3-c2d1-4768-b3fb-9b000e4aa9c2' as TextId,
      values: [
        {
          id: 'c6e00d48-654f-49f1-937f-7ce3264ce1df' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Nachname',
        },
      ],
    },
  },
  {
    id: 'abf2fda7-a45a-49b6-bad1-1aecedd5b453' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.personalDataBirthdate,
    text: {
      id: '2bbfd73f-9442-4dbc-aaf1-bba6f9e26405' as TextId,
      values: [
        {
          id: '51ab7dbb-7c69-44ac-98a8-d4a8ce89d4f7' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Geburtsdatum',
        },
      ],
    },
  },
  {
    id: '75aa5071-35d0-4d7a-892c-6d575c21b874' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.email,
    text: {
      id: 'f7c98fe0-c2f3-4f6e-bbfe-4b0af2852a20' as TextId,
      values: [
        {
          id: '603483f6-225a-4db5-804c-589dbafa608a' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'E-Mail',
        },
      ],
    },
  },
  {
    id: '4be550aa-b3a2-4e22-b839-cc0ea4fc770a' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.newCustomerTelephone,
    text: {
      id: '7d0b6118-963b-4e2f-92ae-1899908b13bd' as TextId,
      values: [
        {
          id: '5b5531e8-f3fb-44ff-b6aa-13ef1b549628' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Telefon',
        },
      ],
    },
  },
  {
    id: '726641ce-418c-48ac-9303-c8941068cb0c' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.additionalData,
    text: {
      id: '73f2adfd-069f-437c-b5a8-424e4f2e7bba' as TextId,
      values: [
        {
          id: 'ae6c1e25-97db-4968-9115-1aaecc9a6eb9' as TextLanguageValueId,
          value: 'Weitere Angaben',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '847a3af8-d67f-4fce-85c6-08eecb3f9b10' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.reservation,
    text: {
      id: 'a4ef96cc-7ac1-49c0-9399-f0885a495980' as TextId,
      values: [
        {
          id: '1f750d1c-f0fc-49ed-a3ac-1560ee5ddbf0' as TextLanguageValueId,
          value: 'Reservierung',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '311ad40c-71e1-452b-95bc-b156b1a3be83' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.passengerData,
    text: {
      id: '51c7ff52-d341-4a9f-8749-c961910952da' as TextId,
      values: [
        {
          id: 'ed482335-8ef5-48cb-98ed-a7f7afd88753' as TextLanguageValueId,
          value: 'Fahrgastdaten',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '50a1025d-68e4-426a-b27b-a3c0867ba4e8' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.payment,
    text: {
      id: '89f1785e-98ba-4897-b4d3-75b98bc8055a' as TextId,
      values: [
        {
          id: '1f7951bd-de02-44ff-9301-1eb26d36048b' as TextLanguageValueId,
          value: 'Kasse',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '691e6d00-6259-4c44-8b0d-2a887c067910' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.checkout,
    text: {
      id: 'e8e11df4-2c57-42be-aa70-21a9104e7e15' as TextId,
      values: [
        {
          id: 'f39f6d80-e998-43fd-b9f8-5b45c0f13547' as TextLanguageValueId,
          value: 'Abschluss',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '0b578ed7-b73f-4405-a1f4-5e2485839c9d' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.vehicleAvailabilityCheck,
    text: {
      id: '8a49da18-c2b1-432d-9916-f73c81d1a241' as TextId,
      values: [
        {
          id: '8727f74b-5c24-4c8e-8a04-cd08061b3a20' as TextLanguageValueId,
          value: 'Fahrzeugverfügbarkeit prüfen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'c7875435-a114-427b-9442-c523c648d818' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.noConnectionAvailable,
    text: {
      id: 'fc4423fd-e4a7-4d8a-8cd1-74e7a8035e4c' as TextId,
      values: [
        {
          id: 'a52c0ed8-fe83-4c45-966a-b8753e665be2' as TextLanguageValueId,
          value: 'Keine Verbindung verfügbar',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '781c7161-cdae-4a51-b6bc-af3e24749dd8' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.additionalInformation,
    text: {
      id: 'b1e69dcf-6458-43fd-9621-bab1e10273a6' as TextId,
      values: [
        {
          id: '73c74e20-f2f8-43db-aa94-8cc4ce0cf3d5' as TextLanguageValueId,
          value: 'Zusätzliche Informationen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'ef816fe0-acd8-4d3f-8219-df72be315894' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.combination,
    text: {
      id: '80ea5a3e-839d-47fd-b926-9dd92fe4b7d2' as TextId,
      values: [
        {
          id: '47051561-879a-46aa-9c82-4d5a89342e9a' as TextLanguageValueId,
          value: 'Bedarfsverkehr-Kombination',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'f98ffcc8-2cda-458b-98a3-26003c7c6778' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandTicket,
    text: {
      id: '458263aa-92cc-4f60-baff-890eccd4cf96' as TextId,
      values: [
        {
          id: 'd09fcd8b-6724-44f5-a79b-8947befa13bd' as TextLanguageValueId,
          value: 'Bedarfsverkehr-Ticket',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '4a4ae5dd-cc8e-4045-b0c7-2bc9cbe97570' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.comfortSurcharge,
    text: {
      id: '0a1c74e7-c4e5-4ff4-bf0c-6b315f363356' as TextId,
      values: [
        {
          id: '6f6f79a9-c570-48dd-b0db-dc4e0469dec4' as TextLanguageValueId,
          value: 'Komfortzuschlag',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '746bf16d-9c71-4d70-a571-370c8c7cdcbb' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.checkVehicle,
    text: {
      id: '78c9c0c5-3b16-47a7-8192-9b84316afab4' as TextId,
      values: [
        {
          id: '2bca45f2-4c17-4a7a-98ab-d46e99893b0e' as TextLanguageValueId,
          value: 'Fahrzeugverfügbarkeit überprüfen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '15db6914-7fb4-4f5d-9cf1-c3caed3a1005' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandRequestingLoading,
    text: {
      id: '7902db43-c5ea-4dff-99da-31eaf0e6066e' as TextId,
      values: [
        {
          id: '84c56ddd-16cd-4b60-a120-02b4bf9051bb' as TextLanguageValueId,
          value: 'Bedarfsverkehr wird angefragt...',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '6c687543-15ce-40e4-ac31-d93ff3c8ce24' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.checkingVehicleLoading,
    text: {
      id: 'a437f0de-e6ef-4ae7-a9e9-5a8179926cc0' as TextId,
      values: [
        {
          id: '51c9dc63-9834-449e-b4e6-4e1ac8d76352' as TextLanguageValueId,
          value: 'Fahrzeugverfügbarkeit wird überprüft...',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'f14a5544-045d-4d40-b615-fe872ace477f' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandBookingLoading,
    text: {
      id: '6d85a982-9186-4dd4-b167-54d401bb72ee' as TextId,
      values: [
        {
          id: '31566dc9-3eae-4f67-b4fd-ee59842bba85' as TextLanguageValueId,
          value: 'Bedarfsverkehr wird gebucht...',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '6af13b7d-3253-4e1e-af95-0772aba6f486' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.noConnection,
    text: {
      id: 'c208bbd0-a4e7-4937-8574-127d3d227313' as TextId,
      values: [
        {
          id: '55ac774e-1e20-4e00-8499-08d100b9e0d3' as TextLanguageValueId,
          value: 'Keine Verbindung verfügbar',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '9c204f0d-062a-47b7-a646-6ba9c2ba1234' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.furtherDetails,
    text: {
      id: '0cf3ef87-0040-4eab-b6de-540f4486cf38' as TextId,
      values: [
        {
          id: '08f1a14b-6924-4419-b239-21197c24fd93' as TextLanguageValueId,
          value: 'Weitere Angaben',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '4e53724b-9ee4-4b96-a9ce-43eeda58cd50' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.passengerType,
    text: {
      id: '3226c968-76c5-4b78-9364-ae1604d8ca3e' as TextId,
      values: [
        {
          id: 'ae913920-3811-4973-9981-9c99f0ba1b40' as TextLanguageValueId,
          value: 'Fahrgasttyp',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '08de058c-db6a-4b50-a539-423fe73d2cfe' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.existingTicket,
    text: {
      id: '7e77ef46-3809-45be-a6a6-b1c3a5d8c2fe' as TextId,
      values: [
        {
          id: '9171160a-3add-490b-a25f-54744acabb9a' as TextLanguageValueId,
          value: 'Vorhandenes Ticket',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '7789a78e-7497-4db9-b909-90c683de3d8f' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.addExtra,
    text: {
      id: 'e74ffcf5-c816-4909-b917-3b672378d046' as TextId,
      values: [
        {
          id: '1a6cea6c-2724-4610-b68b-7418c6a2303c' as TextLanguageValueId,
          value: 'Zusatz hinzufügen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'e4c1c3bc-75d4-43aa-b93c-df193145f234' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.addPassenger,
    text: {
      id: '5ec5924b-1888-4b95-b4ee-f3549d926816' as TextId,
      values: [
        {
          id: '23c771cc-f9fb-4d5b-83f3-5c3280ee091d' as TextLanguageValueId,
          value: 'Weiteren Fahrgast hinzufügen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '1ae6e099-6f3d-4e58-a610-e7a7006d36b8' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.ticketSelection,
    text: {
      id: '475d6abc-052f-429b-a0dc-32ee1a765a90' as TextId,
      values: [
        {
          id: 'ac0a7049-9f67-4d03-8db2-9a8f9a24fd8c' as TextLanguageValueId,
          value: 'Ticketauswahl',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '18080c00-9350-476b-a372-3dbf5a685331' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.actualTimes,
    text: {
      id: '6e1bab34-7866-4afb-87a7-159134f1d890' as TextId,
      values: [
        {
          id: '7fe5d197-f954-46d7-9cf7-e256899030aa' as TextLanguageValueId,
          value: 'Tatsächliche Fahrzeiten',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '456afa59-769c-41d8-8ca4-4cfc4db8fb97' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.deviationWarning,
    text: {
      id: '7f45961d-12e4-4b83-ab9d-d028b92b0006' as TextId,
      values: [
        {
          id: '64bd5e4d-e979-4302-a7e3-3f04116342d8' as TextLanguageValueId,
          value:
            'Bitte beachten Sie, dass sich die Zeiten für den gewählten Bedarfsverkehr geändert haben',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'a0facc64-577c-483b-ae4b-ae3e087c95e5' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.reservedUntil,
    text: {
      id: 'dd6f2f95-f285-4a85-b579-0cd61c9c4b6a' as TextId,
      values: [
        {
          id: 'eacab8b8-042b-4f0a-ba0f-18a72ee81756' as TextLanguageValueId,
          value: 'Diese Fahrt ist bis X reserviert.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '99f91224-fe63-4aa4-b710-464fd9e3f5ba' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.existingTicketActive,
    text: {
      id: '134ff954-e90f-415a-89b8-ef933a88c86a' as TextId,
      values: [
        {
          id: '8f7ba0aa-827a-46c7-bf47-cdc9c637a8de' as TextLanguageValueId,
          value: 'Vorhandenes Ticket aktiv',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '4ac48d54-39bb-48b7-b179-0c506080e91c' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.comfortSurchargeLabel,
    text: {
      id: '3e4c9362-b319-450b-864f-62e982957a17' as TextId,
      values: [
        {
          id: '243584dd-7100-4a1c-8bf1-e31c9901ce99' as TextLanguageValueId,
          value: 'Komfort-Zuschlag',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '0a9bae07-fa25-459a-87b1-78962a91b14c' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.comfortSurchargeExistingTicket,
    text: {
      id: '57306e22-dab8-492b-9e61-c61459d3fc88' as TextId,
      values: [
        {
          id: '5b515c0d-2edf-43ec-b420-5e71eb8214b2' as TextLanguageValueId,
          value: 'Komfortzuschlag für vorhandenes Ticket',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '735c9f34-8586-478d-8c0b-8ba8182f0e54' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.totalAmount,
    text: {
      id: '24146c3a-2bec-42ae-91b7-570f5cf4fb07' as TextId,
      values: [
        {
          id: '6c26bae1-d2a7-4c27-8929-8a0c70929a4d' as TextLanguageValueId,
          value: 'Gesamtbetrag',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'f2cbd093-802f-45ba-b270-f0053ddfb7c9' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.next,
    text: {
      id: 'ef060bd4-85fd-402a-b300-843ba6d9a287' as TextId,
      values: [
        {
          id: 'b4c05df7-5cd4-49f1-903f-d510345d66b1' as TextLanguageValueId,
          value: 'Weiter',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'f5ecc4dc-6522-4132-abd5-a7620373e8f9' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandReservation,
    text: {
      id: '97f34afe-cb97-44e6-83b7-91967646cf1d' as TextId,
      values: [
        {
          id: '945e81cc-a15e-4f58-b3cf-d0dfc90a260e' as TextLanguageValueId,
          value: 'Reservierung buchen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'b3fc5720-d4f2-4d0d-82fc-96f27a17a621' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.goToCheckout,
    text: {
      id: '323f4e7c-2e42-4314-b900-1d22f2e6b043' as TextId,
      values: [
        {
          id: 'bf06dd00-ccf3-422c-b125-5e697aaa224b' as TextLanguageValueId,
          value: 'Zur Kasse',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'a5ce83ad-3ab4-4e15-806d-09feedf28726' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.thankYou,
    text: {
      id: 'ad5a8231-5bf2-4d08-9184-d790b04f8449' as TextId,
      values: [
        {
          id: '343e3675-413a-40fc-b6a8-a264fddb1d35' as TextLanguageValueId,
          value: 'Vielen Dank für die Bestellung!',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd9b8aecd-6723-4a7c-8644-207afb00c825' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.paymentAddress,
    text: {
      id: '1cbbdf3c-0608-4906-8393-9ef88a6cd0fa' as TextId,
      values: [
        {
          id: '43ce236c-309b-42f3-9d7d-3eb1466edf60' as TextLanguageValueId,
          value: 'Rechnungsadresse',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'ef7df3f0-dbcd-4768-8aba-f635f33873f9' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.customerData,
    text: {
      id: 'bcb388d0-db9c-4535-b32a-ad23b8a7eca1' as TextId,
      values: [
        {
          id: '95249ba5-8a57-4f63-9048-49b779970a06' as TextLanguageValueId,
          value: 'Kundendaten',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'ef08a0e1-baf4-4c31-a6a6-642f2f895a0b' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.personalDetails,
    text: {
      id: '17e7f08c-0fbb-4e57-b590-695a867b32f9' as TextId,
      values: [
        {
          id: 'c3521d07-f860-49ba-a94b-e15b64539335' as TextLanguageValueId,
          value: 'Persönliche Angaben',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '746a223b-13d3-4306-aece-9d9da59617e7' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.availableDepartures,
    text: {
      id: 'd9deef7e-8f25-4375-80c2-601f5c21f853' as TextId,
      values: [
        {
          id: '42e76473-f3ac-4658-8c61-eea5853e60b1' as TextLanguageValueId,
          value: 'Verfügbare Abfahrtszeiten',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '7e59b711-78fb-429e-a6d8-37552f136d71' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.chooseConnection,
    text: {
      id: 'ba1d759d-cec8-46f9-9072-9fb37b91d13c' as TextId,
      values: [
        {
          id: 'a6abf49d-221c-4ad5-8109-8e3a8d30f953' as TextLanguageValueId,
          value: 'Verbindung auswählen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '97737ebf-117d-4c94-957c-800d82e0fd26' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandExtras,
    text: {
      id: '1f52fe64-41f0-4926-9dc8-11251473a0ec' as TextId,
      values: [
        {
          id: 'cb6fca55-ad10-469f-9218-9a9a1faff3e1' as TextLanguageValueId,
          value: 'Zusatzoptionen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '2fceed89-68a7-47ea-81be-17f8fac75527' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandExtra,
    text: {
      id: 'a46c1fc7-cddb-4ab3-be00-436c22509d4d' as TextId,
      values: [
        {
          id: '77d8a339-4f35-4ecb-bfa9-7d67d28af197' as TextLanguageValueId,
          value: 'Zusatzoption',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '99157769-5c94-4890-b9b8-37089ef85403' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.chooseExtra,
    text: {
      id: 'b4e6aa16-f5a0-4439-91c0-51a9e28e8376' as TextId,
      values: [
        {
          id: 'bc719fac-2fbc-45dd-8bfb-346b59329d5c' as TextLanguageValueId,
          value: 'Zusatzoption auswählen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'bfafd662-5b8f-46f0-b19b-aaf247ec8881' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.noSpace,
    text: {
      id: '764a56f5-8277-40a9-a732-249dfed3f1f2' as TextId,
      values: [
        {
          id: 'e5472ec4-ab1c-4222-8e06-3b80449a6df3' as TextLanguageValueId,
          value: 'Kein Platz mehr',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'be6d3de3-bb8b-4c38-ac3c-622bc403ff1e' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.remaining,
    text: {
      id: '7da33e6e-21d8-4fc6-8ab0-6ec7fc533b88' as TextId,
      values: [
        {
          id: '24ebc13a-c15e-411d-bbd8-1248bfeddd22' as TextLanguageValueId,
          value: 'noch',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '38543713-90aa-4a0a-9dd1-ec30f76509eb' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandPassenger,
    text: {
      id: 'cbdb0236-601c-45ae-86b3-5871d5689a65' as TextId,
      values: [
        {
          id: 'f91fced7-609f-495a-9cc5-9cc37e6b0ae2' as TextLanguageValueId,
          value: 'Fahrgast',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '279f40d2-bbd0-4e20-9038-3369c19a5fe8' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandOrderOperator,
    text: {
      id: 'e33a584a-29c7-4662-8eb6-658938fb3167' as TextId,
      values: [
        {
          id: '602465de-9b45-4fe8-9e7e-2064ddeb1b12' as TextLanguageValueId,
          value: 'Verkehrsunternehmen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd665aa40-c857-4966-98f3-e38e1a5a44e6' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandOrderChangeableUntil,
    text: {
      id: 'b88e7285-2d9f-4ce3-ac42-00fb30ad99d9' as TextId,
      values: [
        {
          id: 'd813d16a-c5fd-4680-96e9-25378efbd28a' as TextLanguageValueId,
          value: 'Umbuchbar bis',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '4d35709e-2532-4b5a-afbb-09f0722f777e' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandOrderCancellableUntil,
    text: {
      id: 'f99c68b4-494e-4ed3-8217-8d8e8247daea' as TextId,
      values: [
        {
          id: '6d471076-99d5-491c-8041-1aee99737e8e' as TextLanguageValueId,
          value: 'Stornierbar bis',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '0ffcbb57-9110-4d68-bac4-038c29072904' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandOrderNotCancellable,
    text: {
      id: '622b9761-ac42-4dac-8424-0274cbcce3f0' as TextId,
      values: [
        {
          id: 'b272edd1-6c5a-4c46-bded-e207d3517679' as TextLanguageValueId,
          value: 'Nicht stornierbar',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '775b8310-4ebd-4560-90d8-1c8526c3e764' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandOrderExistingPublicTransportTickets,
    text: {
      id: '533e490b-4c51-4224-9e96-a345ad18e07d' as TextId,
      values: [
        {
          id: 'd97d86a9-760e-47c1-abe5-d67477ae05f1' as TextLanguageValueId,
          value: 'Vorhandene ÖPNV-Tickets',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd92eb8c2-72d1-4d94-8279-e6a597ac555c' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.attention,
    text: {
      id: '04a8fb5a-585c-4e9e-8a5c-7e95b65af0be' as TextId,
      values: [
        {
          id: '848cb18c-8f18-43b9-93da-3f4d68b6ca70' as TextLanguageValueId,
          value: 'Achtung',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'a0dbd3b8-0cbe-4acd-a19c-dfbf74b38894' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandHint,
    text: {
      id: '2df7eb54-cfe0-4a06-8dfa-e594b909d1ca' as TextId,
      values: [
        {
          id: 'f4d90feb-c39a-4cd2-944c-acb47c5a11ad' as TextLanguageValueId,
          value:
            'Die gewählte Abfahrtszeit hat sich durch die Zusätze geändert oder ist bereits ausgebucht.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '47e18abf-1590-4432-b679-676763610603' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandExistingTicketHintHeadline,
    text: {
      id: 'e3a5afc7-1b9f-4a11-9fd0-9e0f00d2b012' as TextId,
      values: [
        {
          id: '996fa375-c628-4f74-a238-242ef281cc30' as TextLanguageValueId,
          value: 'Hinweis',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '6c9321eb-b8e2-45a6-8cb2-9e6c108a3e9f' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandExistingTicketHint,
    text: {
      id: 'af29a723-1100-4f84-b523-3729fec1d238' as TextId,
      values: [
        {
          id: 'cf957c0a-d0b4-4d73-b46b-dccc1e354e22' as TextLanguageValueId,
          value:
            'Durch das gewählte Ticket entfällt das Einzelticket. Für den Bedarfsverkehr wird ein Komfortzuschlag erhoben.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '09a3de23-0c65-4b77-bdb2-695e70c9ed3d' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandEntfaellt,
    text: {
      id: '2a386a77-1d0d-4064-ba30-d770df247ea4' as TextId,
      values: [
        {
          id: '901772e1-e621-451f-9826-9c5c6dfe4f92' as TextLanguageValueId,
          value: 'Entfällt',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '5abe0fb8-5b75-4892-a8b9-d2e0b9e812a1' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandTicketNumber,
    text: {
      id: '7675f0fb-e78e-43f2-9533-c78be162a754' as TextId,
      values: [
        {
          id: '3d41747e-044e-4e3b-a7cf-a3f308413d2a' as TextLanguageValueId,
          value: 'Ticket',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '0e15077c-01b8-4833-a325-7d5d4a07c817' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandTripDate,
    text: {
      id: 'bbc4b345-0331-493e-a5b9-c7c33e01f0a4' as TextId,
      values: [
        {
          id: '648f8ca8-ad50-4452-8a07-511c5bde2a3c' as TextLanguageValueId,
          value: 'Fahrt am',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'a96b7d18-a5d4-4440-9fe9-a092f96a5410' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.ticketIsForMe,
    text: {
      id: 'a4843b12-c1f2-42e0-82b5-f9367c403f79' as TextId,
      values: [
        {
          id: '3a592311-cec4-47c4-b75e-7df19915c155' as TextLanguageValueId,
          value: 'Das Ticket ist für mich',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd9a2c4e1-7b8f-4a3d-9c6e-2f5b8a1c9d3e' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.registryTos,
    text: {
      id: '5e8f3b2a-1c9d-4e7f-8a6b-3d2c9e1f4a7b' as TextId,
      values: [
        {
          id: 'b7c4d1e9-2f5a-4b8c-9e3d-7a6f2c1b8e9d' as TextLanguageValueId,
          value:
            'Mit der Registrierung erkläre ich mich sowohl mit den Allgemeinen Geschäftsbedingungen als auch mit der Datenschutzerklärung einverstanden.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'f47ac10b-58cc-4372-a567-0e02b2c3d479' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.noExistingTicketLabel,
    text: {
      id: '3b92f76c-3e61-422f-897c-9b8e8f2a11b5' as TextId,
      values: [
        {
          id: '8a4f91e2-6c34-4b5d-91b1-7f8e3c9a0d12' as TextLanguageValueId,
          value: 'Keine Angabe',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '6fa292ef-eed1-4ba5-9029-de8951db407d' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandPaymentMethod,
    text: {
      id: 'f13de352-ce89-4be3-ad49-6bbd1666b48a' as TextId,
      values: [
        {
          id: '6571e6bf-1e11-4bec-aae4-b02a0396ab8d' as TextLanguageValueId,
          value: 'Zahlungsart',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'e2d936ab-47d5-454f-ba1c-753fdd57cd75' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandAddPaymentMethod,
    text: {
      id: '404ad16f-90e7-4016-aab4-b3cfde2a00cd' as TextId,
      values: [
        {
          id: 'aaefa158-6e2c-49f5-859e-1b97dba7e7d9' as TextLanguageValueId,
          value: 'Neue Zahlungsart hinzufügen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'ddefe271-5b9a-429b-814e-58c17c2c5be3' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandPaymentFailed,
    text: {
      id: '0b04bec4-1706-47ef-8e59-9d402da5e7c6' as TextId,
      values: [
        {
          id: '74690e5f-b429-4bc4-bf5d-8e25eda8dec0' as TextLanguageValueId,
          value:
            'Die Zahlung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '92ecfc11-29ca-41df-bad6-4a451bf8f150' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandCompleteOrder,
    text: {
      id: '63708cd0-de0a-4138-bd2b-0b07d684f27f' as TextId,
      values: [
        {
          id: 'f0679201-c220-43da-8d5c-208f26955ca2' as TextLanguageValueId,
          value: 'Zahlungspflichtig bestellen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '23e1aafb-a53a-40e5-b406-fc1e184049f5' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandOrderBackToWebshop,
    text: {
      id: '92229924-90c4-4075-a2e8-dd7d36de716a' as TextId,
      values: [
        {
          id: '7189e48b-92b9-4963-aca9-b5e37f7e7160' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Zurück zum Webshop',
        },
      ],
    },
  },
  {
    id: 'b70ac285-a09a-4671-96b0-9cea74341324' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandOrderTechnicalCancellationReason,
    text: {
      id: 'fb924d3a-827c-40d6-9ef5-1f9233794499' as TextId,
      values: [
        {
          id: '8949de3d-46a3-4ed9-b8be-68ae9cc9b68e' as TextLanguageValueId,
          value:
            'Die Bestellung konnte aufgrund eines technischen Fehlers nicht vollständig abgeschlossen werden.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'ed5eaa01-0cae-429a-ae41-75af1ae605ef' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.onDemandTicketConfiguration,
    relationType: ERelationType.onDemandOrderCancellationCompleted,
    text: {
      id: '1f8e76a1-de80-434e-abf6-c3b6e7197bcb' as TextId,
      values: [
        {
          id: '671f59f5-8d3d-48bd-90e8-8936ecf24a6b' as TextLanguageValueId,
          value:
            'Die On-Demand-Bestellung wurde storniert. Ein bereits gezahlter Betrag wird erstattet.',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'c673eb35-8111-43f2-8006-26b83896d58c' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.orderHistoryCancellableUntil,
    text: {
      id: 'ffda1db9-3256-42a6-99c0-e10e4151a6ad' as TextId,
      values: [
        {
          id: 'c85a82d1-8a5c-4f10-876e-643bf48071f3' as TextLanguageValueId,
          value: 'Stornierbar bis',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '41dc7a26-2bc0-457d-a0cb-bd4feaf1a689' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.orderHistoryCancellationReasonLabel,
    text: {
      id: 'b8d1bff5-7e7d-4991-889f-b9922a5bb16b' as TextId,
      values: [
        {
          id: '9fa2549b-3281-4aed-86b6-9de9a6e12d64' as TextLanguageValueId,
          value: 'Grund',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '97223a56-1cec-44e9-afd2-c577f53789b9' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.cancelOrderMail,
    relationType: ERelationType.cancelOrderMail,
    text: {
      id: '2459f35b-9026-4ad5-98f0-55aa7eb91b1e' as TextId,
      values: [
        {
          id: 'ce7f1a79-d698-4364-87ee-b5a6a8229aff' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value:
            'Bei der Verarbeitung Ihrer Bestellung ist ein technischer Fehler aufgetreten.',
        },
      ],
    },
  },
];

export const DEFAULT_APP_TEXTS: INormalText[] = [
  {
    id: 'bf8a634e-2145-4067-86b3-93a84e3b5ff1' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.loginLogout,
    relationType: ERelationType.loginButton,
    text: {
      id: '6896f1bf-30bd-4edd-b0f6-9dc093ee9880' as TextId,
      values: [
        {
          id: '77ec9149-5b7f-49bf-aba2-8564d172672c' as TextLanguageValueId,
          value: 'Anmelden',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '39fa34f4-dfdd-4862-8981-36612f56dd8c' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.loginLogout,
    relationType: ERelationType.loginHeader,
    text: {
      id: 'dbcbd299-1957-4aff-809b-22c6dfbc57cd' as TextId,
      values: [
        {
          id: 'e3b926fa-d51a-4383-8654-f492d641f73d' as TextLanguageValueId,
          value: 'Mit Kundenkonto anmelden',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'bdfa148a-9947-43d2-9077-afbd515fb44b' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.loginLogout,
    relationType: ERelationType.email,
    text: {
      id: '69c43c02-e41f-4890-81a2-2a2833b5efd9' as TextId,
      values: [
        {
          id: '41216e84-a14a-4c49-ad8e-fbcd846a6b49' as TextLanguageValueId,
          value: 'E-Mail Adresse',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '03954a1e-bbc5-47d1-9ba9-12f8256a3416' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.loginLogout,
    relationType: ERelationType.password,
    text: {
      id: 'ac4dbff0-d444-432b-9ecc-fdcd3f1a9c2a' as TextId,
      values: [
        {
          id: 'bcab080c-5c33-4678-bada-383fb1fd9ccb' as TextLanguageValueId,
          value: 'Passwort',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '620cb960-4fae-449e-9f63-e2ef21be4ff8' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.loginLogout,
    relationType: ERelationType.separation,
    text: {
      id: '26d4da03-353e-4c7d-8ed2-e3404bfcda6e' as TextId,
      values: [
        {
          id: '872962e2-ef37-4f62-a5e5-8df8f9d64841' as TextLanguageValueId,
          value: 'oder',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '1508619d-7b14-4e35-88db-86f4a4d2206c' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.loginLogout,
    relationType: ERelationType.register,
    text: {
      id: 'e60342ec-d3e7-4561-961d-1feec4ad221e' as TextId,
      values: [
        {
          id: '83380e51-792b-49ff-95ee-58cbee422a00' as TextLanguageValueId,
          value: 'Registrieren',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd47538bf-4e40-4425-b2f8-d53ab27b7140' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.loginLogout,
    relationType: ERelationType.passwordReset,
    text: {
      id: '4019be79-c484-4f2a-b099-586e59834443' as TextId,
      values: [
        {
          id: '9e6df005-3039-4571-8522-596438180b6c' as TextLanguageValueId,
          value: 'Passwort vergessen?',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd8e9d7f6-32d4-492c-8627-9ac8f8592cf9' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.loginLogout,
    relationType: ERelationType.passwordResetLink,
    text: {
      id: '9423a08e-029d-4267-85ba-4670bf89367f' as TextId,
      values: [
        {
          id: 'd39daa98-bdef-48ec-bee1-2c27773288db' as TextLanguageValueId,
          value: 'Passwort zurücksetzen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd38dd4c1-2c29-42b0-b9a1-49bce213c731' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.startScreen,
    relationType: ERelationType.otherTicketsSectionTitle,
    text: {
      id: '7e32684a-2045-4fc5-845f-4424b1a3a94c' as TextId,
      values: [
        {
          id: '7898739e-3f1d-4b5c-a9f2-90ace8a754a7' as TextLanguageValueId,
          value: 'Mehr Tickets und Angebote',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'dc3d28ab-9917-4016-9f30-7e8bfc13b2bd' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.startScreen,
    relationType: ERelationType.fixedPriceTileTitle,
    text: {
      id: 'a496a4e5-0253-4c32-8e49-3b4d755ce615' as TextId,
      values: [
        {
          id: 'c8cd57a2-1c2d-4315-b4b7-d1235dbf1aab' as TextLanguageValueId,
          value: 'Regional- oder Zeitkarten',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '0a57dc8d-eca1-48ac-8d37-4d5e3eba7f43' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.startScreen,
    relationType: ERelationType.fixedPriceTileSubtitle,
    text: {
      id: '00d6a175-281b-4a99-a47c-8310fa805869' as TextId,
      values: [
        {
          id: '32abb253-49c0-4ec6-9e7f-b0cf4bf230b3' as TextLanguageValueId,
          value: 'Bayern, Spezial und Sonstige',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '00139003-feba-4d60-ae82-444a3841e9cb' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.header,
    relationType: ERelationType.fixedPriceTicketTitle,
    text: {
      id: '765d6d44-cd4e-4766-80ee-5cb41e8a7814' as TextId,
      values: [
        {
          id: '6e9cabba-911a-41a8-b5d6-c40ef4e9749a' as TextLanguageValueId,
          value: 'Ticket wählen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '8c47d59b-50f7-491d-97f5-e9a03ed293ea' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.loginLogout,
    relationType: ERelationType.passwordResetDisabled,
    text: {
      id: '0231cddb-c08c-4830-9a45-6ffd6aafe34b' as TextId,
      values: [
        {
          id: '4d2437b5-ee9a-4746-a423-c88f4d5f71d7' as TextLanguageValueId,
          value: 'Passwort kann nicht zurückgesetzt werden',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'c11e8e86-b1a8-4283-86fb-cf4fdaf058eb' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.generic,
    relationType: ERelationType.saveDisabled,
    text: {
      id: '23724a6e-9e0b-49a4-b68a-7dfdf5354e76' as TextId,
      values: [
        {
          id: '09c2d207-4999-4fcc-ae1d-fb7aa5d5d931' as TextLanguageValueId,
          value: 'Speichern nicht möglich',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd8b357a6-99bb-47c6-8b52-e15c8cd9b3d8' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.generic,
    relationType: ERelationType.backDisabled,
    text: {
      id: 'ab8aeadf-1c34-4e19-8356-1c2397370305' as TextId,
      values: [
        {
          id: '797ad1c0-dbad-489d-bd0b-0664e017c3dc' as TextLanguageValueId,
          value: 'Zurücknavigation nicht möglich',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '0337339c-dabe-4152-af21-ba8d01da9163' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.generic,
    relationType: ERelationType.continueTicketConfigDisabled,
    text: {
      id: '25262026-c3fe-491f-93dc-ef5f99af4ef5' as TextId,
      values: [
        {
          id: 'e461c4ad-6375-4f71-bafb-3bb55d36efdb' as TextLanguageValueId,
          value: 'Weitere Ticketkonfiguration nicht möglich',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: 'd7aa46d6-6df4-4919-9bc3-62af0b73feb4' as NormalTextId,
    textType: ETextType.App,
    entityType: EEntityType.generic,
    relationType: ERelationType.addToConfigurationDisabled,
    text: {
      id: '49ce0931-2b14-43cd-81ad-5727877f1795' as TextId,
      values: [
        {
          id: '20c95b76-32ac-422e-949d-34915eaeb769' as TextLanguageValueId,
          value: 'Weiteres Ticket kann nicht hinzugefügt werden',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '0a7553aa-d0cc-4399-a2a0-3ab08d5e1718' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.orderHistoryResend,
    text: {
      id: '5853fdbe-66de-41de-ab29-47d9b8806bce' as TextId,
      values: [
        {
          id: 'd614edb0-53dc-49f4-8f80-a0674d717741' as TextLanguageValueId,
          value: 'Erneut senden',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '373ee3f0-6b69-441e-a2cd-8744d53bc07d' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.generic,
    relationType: ERelationType.orderHistoryDownload,
    text: {
      id: '934573b2-2f48-45e1-b6da-bc009870c7e0' as TextId,
      values: [
        {
          id: 'a4129d11-b6a3-47f6-b672-f29cb43a4231' as TextLanguageValueId,
          value: 'Herunterladen',
          languagePrefix: 'DE' as LanguagePrefix,
        },
      ],
    },
  },
  {
    id: '8d5fd01c-e335-4b7e-8c71-6cfdadf4e2a1' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.fellowTravelersLabel,
    text: {
      id: 'ea98bcfe-2f05-4d55-97c4-f4898e19a91c' as TextId,
      values: [
        {
          id: '7f1c7efc-d3ae-46fa-8857-b8e120966cf4' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Mitreisende',
        },
      ],
    },
  },
  {
    id: '00cbfa8e-1e60-49a6-9f32-4627a184dd72' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.deleteFellowTravelerButtonText,
    text: {
      id: 'c54ed4cd-161d-4f34-b804-0eeaff613a0a' as TextId,
      values: [
        {
          id: 'e5aa180d-515e-43fb-8978-c0928e5a81d0' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Mitreisenden löschen',
        },
      ],
    },
  },
  {
    id: '4e9cc35a-ec28-46ea-bf90-3c52c819a6bc' as NormalTextId,
    textType: ETextType.Web,
    entityType: EEntityType.privateArea,
    relationType: ERelationType.createFellowTravelerButtonText,
    text: {
      id: '05788c46-4df7-416c-b772-6355633d8019' as TextId,
      values: [
        {
          id: 'f4038903-1ae1-4499-a826-e98d55636650' as TextLanguageValueId,
          languagePrefix: 'DE' as LanguagePrefix,
          value: 'Mitreisenden erstellen',
        },
      ],
    },
  },
];

export const DEFAULT_TEXTS: INormalText[] = [
  ...DEFAULT_WEB_TEXTS,
  ...DEFAULT_APP_TEXTS,
];

export const getDefaultTextValue = (
  entityType: EEntityType,
  relationType: ERelationType,
  textType: ETextType,
  languagePrefix: LanguagePrefix
): string | undefined =>
  DEFAULT_TEXTS.find(
    (text: INormalText): boolean =>
      text.entityType === entityType &&
      text.relationType === relationType &&
      text.textType === textType
  )?.text.values.find(
    (value: ITextLanguageValue | ITextLanguageChangedValue): boolean =>
      value.languagePrefix === languagePrefix
  )?.value;
